/**
 * Treatment Progress Modal
 *
 * Step-by-step wizard showing treatment progression details.
 * Uses horizontal step indicator for navigation and gated sequential flow.
 * Allows marking steps as complete/skip, adding medications, and tracking progress.
 * Backed by persisted data from the cycle storage.
 */

import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { getTodayString } from "@src/packages/utils/date";
import {
  View,
  Modal,
  Pressable,
  ScrollView,
  TextInput,
  Text as RNText,
  Platform,
  Animated as RNAnimated,
  StyleSheet,
} from "react-native";
import type { TextProps as RNTextProps } from "react-native";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MoonDateField, MoonTimeField, MoonFieldInput } from "@louise/moon-ui";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Plus,
  X,
  Pill,
  ExternalLink,
  AlertCircle,
  Clock,
  Zap,
  RotateCcw,
  MoreVertical,
  Trash2,
  ChevronDown,
} from "lucide-react-native";
import { LinearGradient } from "expo-linear-gradient";
import type {
  CycleType,
  TreatmentStepProgress,
  TreatmentStepStatus,
  LocalMedication,
} from "@louise/care/treatment-phases";
import { useDeleteMedication } from "@louise/care";
import { useMedications } from "@louise/care/hooks/useMedications";
import { useApiClient } from "@src/packages/api/louise-api";
import { throwApiError } from "@src/packages/api/api-error";
import {
  usePhases,
  useCreatePhase,
  getPhaseTypeForStep,
} from "@src/packages/patients/hooks/usePhases";
import type { PgtPlan } from "@src/packages/cycles/presenters/resolveConditionalCycleGraph";
import {
  getStepsForCycle,
  tf,
  type TreatmentStepConfig,
} from "./treatmentFlows";
import type { TreatmentMedicationContext } from "@src/packages/navigation/types";
import { useLocale, useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { formatDate } from "@src/packages/i18n/formatters";
import { ConfirmationModal } from "./NextStepCTA";
import { TreatmentStepIndicator } from "./TreatmentStepIndicator";
import { TreatmentUploadField } from "./TreatmentUploadField";
import { CarePillActionButton } from "../plan/components/CarePillActionButton";

// The flows and the graph that resolves them live in `./treatmentFlows` so they
// can be read without a React Native runtime. Re-exported here because this
// module has always been where the rest of the app imports them from, and
// moving them is not a reason to touch every call site.
export { getStepsForCycle, getStepsForPerspective } from "./treatmentFlows";
export type {
  ConditionalCycleOptions,
  StepPerspective,
  TreatmentStepConfig,
} from "./treatmentFlows";

type DMSansTextProps = RNTextProps & { className?: string };

function resolveDMSansFamily(fontWeight: unknown) {
  const weight =
    typeof fontWeight === "number"
      ? fontWeight
      : typeof fontWeight === "string"
        ? Number.parseInt(fontWeight, 10)
        : 400;

  if (Number.isFinite(weight) && weight >= 700) return "DMSans_700Bold";
  if (Number.isFinite(weight) && weight >= 500) return "DMSans_500Medium";
  return "DMSans_400Regular";
}

function Text({ className, style, ...rest }: DMSansTextProps) {
  const flattenedStyle = StyleSheet.flatten(style);
  const fontFamily = resolveDMSansFamily(flattenedStyle?.fontWeight);

  return (
    <RNText
      {...rest}
      className={["font-dm-sans", className].filter(Boolean).join(" ")}
      style={[{ fontFamily }, style, { fontFamily }]}
    />
  );
}

// =============================================================================
// Types & Configuration
// =============================================================================

/** Step definition for a treatment flow */

// Color constants
const violetAccent = "#C4B5FD";
const greenAccent = "#4ADE80";

// =============================================================================
// Date Formatting Helper
// =============================================================================

function formatMedicationDate(dateString: string): string | null {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;

    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const month = months[date.getMonth()];
    const day = date.getDate();

    return `${month} ${day}`;
  } catch {
    return null;
  }
}

// =============================================================================
// DateTime Field Picker Component
// =============================================================================

interface DateTimeFieldPickerProps {
  value: string | null;
  onChange: (isoString: string | null) => void;
  placeholder?: string;
}

export function DateTimeFieldPicker({
  value,
  onChange,
  placeholder,
}: DateTimeFieldPickerProps) {
  const dateString = useMemo(() => {
    if (!value || typeof value !== "string") return "";
    const date = new Date(value);
    if (isNaN(date.getTime())) return "";
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }, [value]);

  const timeString = useMemo(() => {
    if (!value || typeof value !== "string") return "";
    const date = new Date(value);
    if (isNaN(date.getTime())) return "";
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${hours}:${minutes}`;
  }, [value]);

  const handleDateChange = useCallback(
    (nextDate: string) => {
      if (!nextDate) {
        onChange(null);
        return;
      }
      const [hours, minutes] = (timeString || "12:00").split(":").map(Number);
      const combined = new Date(`${nextDate}T00:00:00`);
      combined.setHours(hours ?? 12, minutes ?? 0, 0, 0);
      onChange(combined.toISOString());
    },
    [timeString, onChange],
  );

  const handleTimeChange = useCallback(
    (nextTime: string) => {
      const baseDateString =
        dateString ||
        (() => {
          const today = new Date();
          const y = today.getFullYear();
          const m = String(today.getMonth() + 1).padStart(2, "0");
          const d = String(today.getDate()).padStart(2, "0");
          return `${y}-${m}-${d}`;
        })();
      const [hours, minutes] = (nextTime || "12:00").split(":").map(Number);
      const combined = new Date(`${baseDateString}T00:00:00`);
      combined.setHours(hours ?? 12, minutes ?? 0, 0, 0);
      onChange(combined.toISOString());
    },
    [dateString, onChange],
  );

  return (
    <View className="flex-row gap-2">
      <View style={{ flex: 1 }}>
        <MoonDateField
          label="Date"
          placeholder={placeholder ?? "Select date"}
          value={dateString}
          onChange={handleDateChange}
        />
      </View>
      <View style={{ flex: 1 }}>
        <MoonTimeField
          label="Time"
          placeholder="HH:MM"
          value={timeString}
          onChange={handleTimeChange}
        />
      </View>
    </View>
  );
}

// =============================================================================
// Step Wizard Content Component
// =============================================================================

const TRIGGER_DEFAULT_DOSES: Record<string, string> = {
  Ovidrel: "250 mcg",
  Pregnyl: "10,000 IU",
  Novarel: "10,000 IU",
};

const TRIGGER_DOSE_UNITS = ["mcg", "IU", "mg", "units"] as const;

// Splits a stored "250 mcg" / "10,000 IU" combined dose string into the
// amount and unit halves so the picker can edit each independently.
function parseDoseString(value: string): { amount: string; unit: string } {
  const trimmed = value.trim();
  if (!trimmed) return { amount: "", unit: "" };
  const match = /^([\d.,]+)\s*(.*)$/.exec(trimmed);
  if (!match) return { amount: "", unit: trimmed };
  return { amount: match[1] ?? "", unit: (match[2] ?? "").trim() };
}

function joinDose(amount: string, unit: string): string {
  const a = amount.trim();
  const u = unit.trim();
  if (a && u) return `${a} ${u}`;
  return a || u;
}

function DoseField({
  value,
  onChange,
  placeholder,
  dropdownKey,
  dropdownOpen,
  setDropdownOpen,
  colors,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  dropdownKey: string;
  dropdownOpen: string | null;
  setDropdownOpen: (next: string | null) => void;
  colors: {
    fieldBg: string;
    fieldBorder: string;
    fieldText: string;
    fieldPlaceholder: string;
    selectedBorder: string;
    label: string;
    dropdownBg: string;
    dropdownBorder: string;
    dropdownItem: string;
    dropdownSep: string;
    fieldSelected: string;
  };
}) {
  const { amount, unit } = parseDoseString(value);
  const isOpen = dropdownOpen === dropdownKey;
  const capturePress = usePressCapture("DoseField");
  return (
    <View style={{ position: "relative" }}>
      <View className="flex-row">
        <TextInput
          value={amount}
          onChangeText={(text) => onChange(joinDose(text, unit))}
          placeholder={placeholder}
          placeholderTextColor={colors.fieldPlaceholder}
          keyboardType="decimal-pad"
          style={{
            flex: 1,
            backgroundColor: colors.fieldBg,
            borderWidth: 1,
            borderColor: colors.fieldBorder,
            borderTopLeftRadius: 8,
            borderBottomLeftRadius: 8,
            borderRightWidth: 0,
            paddingHorizontal: 12,
            paddingVertical: 8,
            fontSize: 13,
            color: colors.fieldText,
          }}
        />
        <Pressable
          onPress={() => {
            capturePress(pressLabel(unit || "unit"), "dose_unit_dropdown");
            setDropdownOpen(isOpen ? null : dropdownKey);
          }}
          className="flex-row items-center justify-center"
          style={{
            gap: 4,
            paddingHorizontal: 12,
            borderWidth: 1,
            borderColor: isOpen ? colors.selectedBorder : colors.fieldBorder,
            borderTopRightRadius: 8,
            borderBottomRightRadius: 8,
            backgroundColor: colors.fieldBg,
            minWidth: 70,
          }}
        >
          <Text
            style={{
              fontSize: 13,
              color: unit ? colors.fieldText : colors.fieldPlaceholder,
            }}
          >
            {unit || "unit"}
          </Text>
          <ChevronDown size={14} color={colors.label} />
        </Pressable>
      </View>
      {isOpen && (
        <View
          style={{
            marginTop: 4,
            backgroundColor: colors.dropdownBg,
            borderWidth: 1,
            borderColor: colors.dropdownBorder,
            borderRadius: 8,
            overflow: "hidden",
          }}
        >
          {TRIGGER_DOSE_UNITS.map((opt) => {
            const isSelected = unit === opt;
            return (
              <Pressable
                key={opt}
                onPress={() => {
                  capturePress(pressLabel(opt), "dose_unit_option");
                  onChange(joinDose(amount, opt));
                  setDropdownOpen(null);
                }}
                className="px-3 py-2.5"
                style={{
                  backgroundColor: isSelected
                    ? `${colors.fieldSelected}18`
                    : "transparent",
                  borderBottomWidth: 1,
                  borderBottomColor: colors.dropdownSep,
                }}
              >
                <Text
                  style={{
                    fontSize: 13,
                    color: isSelected
                      ? colors.fieldSelected
                      : colors.dropdownItem,
                    fontWeight: isSelected ? "600" : "400",
                  }}
                >
                  {opt}
                </Text>
              </Pressable>
            );
          })}
        </View>
      )}
    </View>
  );
}

export interface StepWizardContentProps {
  step: TreatmentStepConfig;
  progress: TreatmentStepProgress | null;
  isCurrentStep: boolean;
  onComplete: (data?: Record<string, string | number | boolean | null>) => void;
  onSkip: () => void;
  onUndo: () => void;
  onUpdateData: (
    data: Record<string, string | number | boolean | null>,
  ) => void;
  onAddMedication: () => void;
  onAddTriggerShot?: () => void;
  linkedMedications: LocalMedication[];
  /** Suppress the internal title/status header when embedded inline in HubStepCard */
  hideHeader?: boolean;
  /** Theme variant — "dark" (olly default) or "light" (moon EMBER) */
  theme?: "dark" | "light";
  /** Override the default dark DateTimeFieldPicker with a custom component */
  renderDateTimePicker?: (props: {
    value: string | null;
    onChange: (isoString: string | null) => void;
    placeholder?: string;
  }) => React.ReactNode;
  /** Override the default medication card rendering */
  renderMedicationCard?: (
    med: LocalMedication,
    options: { onMenu: (id: string) => void },
  ) => React.ReactNode;
  /** Override the empty medication state when this wizard is embedded in a section card. */
  renderMedicationEmptyState?: (options: {
    onAddMedication: () => void;
  }) => React.ReactNode;
  /**
   * Override the schedulable-procedure planning UI. When provided and the step
   * is a procedure awaiting scheduling (no date / future date), the bespoke
   * inline date/time form + Schedule CTA are suppressed and this canonical row
   * is rendered instead — it opens the app's normal appointment flow.
   */
  renderScheduleAction?: () => React.ReactNode;
  /**
   * Override the outcome form for a completed/past egg-retrieval procedure.
   * When provided, the scheduling row remains the entry point for a future
   * procedure, while this row is the sole action once retrieval has happened.
   */
  renderOutcomeAction?: () => React.ReactNode;
  /**
   * Override the inline form for a lab-report step (`resultsUpload`). When
   * provided, the bespoke fields + Confirm CTA are suppressed and this
   * canonical row is rendered instead — it opens the app's normal
   * document/test-result upload flow.
   */
  renderResultsAction?: () => React.ReactNode;
  /**
   * Override the inline form for a `detailsModal` step (fertilization, embryo
   * development, freezing, screening, PGT). When provided, the bespoke fields +
   * Confirm CTA are suppressed and this canonical row is rendered instead — it
   * opens StepDetailsModal, which holds the same inputs.
   */
  renderDetailsAction?: () => React.ReactNode;
  /** Render the real specimen selector for IUI collection/procedure steps. */
  renderLinkageAction?: () => React.ReactNode;
  /** Use the current care page section treatment instead of the old wizard prompt/card chrome. */
  presentation?: "modal" | "hub";
  /** Medication add modals persist their own data, so embedded medication steps do not need a second save CTA. */
  hidePendingCompleteAction?: boolean;
  /** Suppress the prompt title/subtitle when the surrounding section already provides context. */
  hidePrompt?: boolean;
  /** Override the primary pending-state action label for embedded contexts. */
  pendingActionLabel?: string;
  /** Hide the skip ("I didn't have…") action — e.g. once the user has logged
   * data that contradicts skipping this step. */
  hideSkip?: boolean;
}

/**
 * Steps whose inputs used to render as a bespoke inline form in the step card
 * now show a canonical row that opens a canonical dialogue (StepDetailsModal)
 * holding the same inputs. Keyed by step id so it applies across every flow.
 * Interim until each step gets its proper redesign (#2094–#2100 / #2041).
 */
const DETAILS_MODAL_STEP_IDS = new Set([
  "screening",
  "fertilization",
  "embryo_development",
  "freezing",
  "pgt_testing",
]);

export function stepUsesDetailsModal(stepId: string): boolean {
  return DETAILS_MODAL_STEP_IDS.has(stepId);
}

function getHubPromptTitle(
  step: TreatmentStepConfig,
  opts?: { isPlanning?: boolean },
) {
  if (step.contentType === "medications") return "Medication details";
  if (opts?.isPlanning && step.schedulingPrompt) return step.schedulingPrompt;
  return step.prompt ?? step.title;
}

function getHubPromptSubtitle(step: TreatmentStepConfig) {
  if (step.id === "pre_treatment") {
    return "Add priming medications used before stimulation.";
  }
  if (step.id === "protocol") {
    return "Add stimulation medications, dose changes, trigger shots, and clinic instructions.";
  }
  if (step.id === "luteal_phase") {
    return "Add support medications used after transfer.";
  }
  if (step.contentType === "medications") return step.description;
  return step.description;
}

export function StepWizardContent({
  step,
  progress,
  isCurrentStep,
  onComplete,
  onSkip,
  onUndo,
  onUpdateData,
  onAddMedication,
  onAddTriggerShot,
  linkedMedications,
  hideHeader = false,
  theme = "dark",
  renderDateTimePicker,
  renderMedicationCard,
  renderMedicationEmptyState,
  renderScheduleAction,
  renderOutcomeAction,
  renderResultsAction,
  renderDetailsAction,
  renderLinkageAction,
  presentation = "modal",
  hidePendingCompleteAction = false,
  hidePrompt = false,
  pendingActionLabel,
  hideSkip = false,
}: StepWizardContentProps) {
  const t = useTranslations();
  const capturePress = usePressCapture("StepWizardContent");
  const isLight = theme === "light";
  const resolvedPendingActionLabel =
    pendingActionLabel ?? t("care.treatment_flows.actions.confirm_save");
  const { locale } = useLocale();
  const isHubPresentation = presentation === "hub";
  const c = isLight
    ? {
        prompt: "#2A2522",
        promptBg: "rgba(139,58,26,0.05)",
        promptBorder: "rgba(139,58,26,0.1)",
        label: "#5A4D45",
        medName: "#2A2522",
        medBg: "rgba(139,58,26,0.04)",
        medBorder: "rgba(139,58,26,0.12)",
        medDose: "#5A4D45",
        medDate: "#8A7D74",
        fieldBg: "rgba(0,0,0,0.03)",
        fieldBorder: "rgba(0,0,0,0.08)",
        fieldText: "#2A2522",
        fieldPlaceholder: "#B0A49A",
        fieldSelected: "#8B3A1A",
        selectedBg: "rgba(139,58,26,0.1)",
        selectedBorder: "rgba(139,58,26,0.3)",
        skipText: "#8A7D74",
        undo: "#8A7D74",
        undoBg: "rgba(0,0,0,0.04)",
        undoBgPressed: "rgba(0,0,0,0.08)",
        accent: "#8B3A1A",
        accentBg: "rgba(139,58,26,0.08)",
        accentBorder: "rgba(139,58,26,0.15)",
        noMedBorder: "rgba(0,0,0,0.1)",
        noMedText: "#8A7D74",
        completedMedText: "#5A4D45",
        descText: "#5A4D45",
        dropdownBg: "rgba(255,255,255,0.97)",
        dropdownBorder: "rgba(0,0,0,0.1)",
        dropdownItem: "#2A2522",
        dropdownSep: "rgba(0,0,0,0.06)",
        customBg: "rgba(255,255,255,0.9)",
        customBorder: "rgba(139,58,26,0.15)",
        customAccent: "#8B3A1A",
        customInputBg: "rgba(0,0,0,0.03)",
        customInputBorder: "rgba(0,0,0,0.1)",
        customInputText: "#2A2522",
        cancelText: "#8A7D74",
        cancelBorder: "rgba(0,0,0,0.1)",
        otherBg: "rgba(0,0,0,0.03)",
        otherBorder: "#C8BDB5",
        otherIcon: "#8A7D74",
        otherText: "#5A4D45",
        secondMedLabel: "#5A4D45",
        menuBg: "#FAF6F2",
        menuBorder: "rgba(0,0,0,0.1)",
        menuText: "#5A4D45",
        menuCancel: "#8A7D74",
      }
    : {
        prompt: "#e2e8f0",
        promptBg: "rgba(196,181,253,0.05)",
        promptBorder: "rgba(196,181,253,0.1)",
        label: "#94a3b8",
        medName: "#e2e8f0",
        medBg: "rgba(2,6,23,0.4)",
        medBorder: "rgba(167,139,250,0.15)",
        medDose: "#94a3b8",
        medDate: "#64748b",
        fieldBg: "rgba(2,6,23,0.5)",
        fieldBorder: "rgba(255,255,255,0.1)",
        fieldText: "#e2e8f0",
        fieldPlaceholder: "#475569",
        fieldSelected: "#fbbf24",
        selectedBg: "rgba(251,191,36,0.2)",
        selectedBorder: "rgba(251,191,36,0.5)",
        skipText: "#64748b",
        undo: "#94a3b8",
        undoBg: "rgba(255,255,255,0.03)",
        undoBgPressed: "rgba(255,255,255,0.08)",
        accent: violetAccent,
        accentBg: "rgba(167,139,250,0.1)",
        accentBorder: "rgba(167,139,250,0.2)",
        noMedBorder: "rgba(255,255,255,0.1)",
        noMedText: "#64748b",
        completedMedText: "#cbd5e1",
        descText: "#94a3b8",
        dropdownBg: "rgba(15,23,42,0.95)",
        dropdownBorder: "rgba(255,255,255,0.1)",
        dropdownItem: "#e2e8f0",
        dropdownSep: "rgba(255,255,255,0.05)",
        customBg: "rgba(15,23,42,0.8)",
        customBorder: "rgba(196,181,253,0.2)",
        customAccent: "#C4B5FD",
        customInputBg: "rgba(15,23,42,0.5)",
        customInputBorder: "rgba(255,255,255,0.1)",
        customInputText: "#ffffff",
        cancelText: "#94a3b8",
        cancelBorder: "rgba(255,255,255,0.1)",
        otherBg: "rgba(30,41,59,0.5)",
        otherBorder: "#334155",
        otherIcon: "#64748B",
        otherText: "#94a3b8",
        secondMedLabel: "#94a3b8",
        menuBg: "#0f172a",
        menuBorder: "rgba(255,255,255,0.1)",
        menuText: "#94a3b8",
        menuCancel: "#64748b",
      };

  const status = progress?.status ?? "pending";
  const savedData = useMemo(() => progress?.data ?? {}, [progress?.data]);
  // Buffer field changes locally — only flushed on "Confirm & Save"
  const [localData, setLocalData] =
    useState<Record<string, string | number | boolean | null>>(savedData);
  // Sync local state when saved data changes (e.g., after a refetch)
  useEffect(() => {
    setLocalData(savedData);
  }, [savedData]);
  const stepData = localData;

  // Are we PLANNING (the procedure hasn't happened yet) or LOGGING
  // (the procedure already happened)? Drives:
  //   - which prompt to show: schedulingPrompt vs prompt
  //   - which validations gate the button: date alone vs all required
  //   - which label the button takes: Schedule vs Confirm & Save
  //
  // Planning when either: no procedure/transfer date has been entered yet,
  // OR the date entered is in the future. Logging when a today/past date
  // is in the buffer (the user is describing what just happened).
  const procedureDate = useMemo<{
    state: "missing" | "future" | "today_or_past";
    dateIso: string | null;
  }>(() => {
    // Local, not UTC, and through the helper this file already imports.
    // `CycleDetailScreen` writes the phase and resolves the calendar sync from
    // the device's own calendar day, so a UTC "today" put the two a day apart for
    // a few hours daily: the card could offer to schedule a date the sync had
    // already decided was past, or call a future one dated.
    const todayIso = getTodayString();
    let seenDateIso: string | null = null;
    // `collectionDate` belongs here as much as the other two: it is the date
    // field `PROCEDURE_SCHEDULING` declares for `sperm_collection`. Left out, a
    // recorded collection date was invisible to every decision below — the step
    // stayed permanently in "planning", so the card kept offering "Schedule the
    // sperm collection" over a collection the patient had already dated, the
    // prompt stayed the scheduling one, and only the date field was allowed to
    // render.
    for (const fieldName of [
      "procedureDate",
      "transferDate",
      "collectionDate",
    ] as const) {
      const raw = localData[fieldName];
      if (typeof raw !== "string" || raw.length < 10) continue;
      const dateOnly = raw.slice(0, 10);
      seenDateIso = dateOnly;
      if (dateOnly > todayIso) return { state: "future", dateIso: dateOnly };
    }
    return seenDateIso
      ? { state: "today_or_past", dateIso: seenDateIso }
      : { state: "missing", dateIso: null };
  }, [localData]);
  const procedureDateState = procedureDate.state;
  // Pending-without-date and future-date both mean "we're planning". Only
  // a today/past date in the buffer means "logging the past event".
  // `schedulable` gates this — lab-outcome steps don't have a planning mode.
  const isPlanningProcedure =
    step.schedulable === true && procedureDateState !== "today_or_past";

  // When a caller supplies a canonical schedule row (moon hub), a procedure
  // awaiting scheduling delegates to the normal appointment flow instead of
  // rendering the inline date/time form + Schedule CTA.
  // Already scheduled through the canonical appointment flow, waiting for
  // the day: the schedule CTA would be a lie ("nothing planned yet"), so the
  // row gives way to a dated confirmation until the date arrives (#3273).
  const showScheduledConfirmation =
    step.schedulable === true &&
    procedureDateState === "future" &&
    renderScheduleAction != null;
  const showScheduleRow =
    isPlanningProcedure &&
    renderScheduleAction != null &&
    !showScheduledConfirmation;
  /**
   * The same line, for a procedure whose date has passed. See the render site for
   * why it states the date rather than the event.
   *
   * `showScheduledConfirmation` stops at `"future"`, so a step the patient had
   * dated in the past showed nothing dated at all: the schedule row is correctly
   * gone, and what took its place was the field list. Scoped to the hub the same
   * way its future twin is — `renderScheduleAction != null` is what says this
   * caller has a canonical scheduling surface at all.
   */
  const showCollectedConfirmation =
    step.schedulable === true &&
    procedureDateState === "today_or_past" &&
    renderScheduleAction != null;

  // Retrieval scheduling and retrieval outcomes are different user actions.
  // Keep the appointment row for a missing/future date, and delegate the
  // happened-state to the caller when it has a canonical outcome surface.
  const showOutcomeRow =
    (step.id === "egg_retrieval" || step.id === "outcome") &&
    !isPlanningProcedure &&
    renderOutcomeAction != null;

  // A lab-report step (semen analysis) delegates to the normal document/
  // test-result upload flow via a canonical row, instead of an inline form.
  const showResultsRow =
    step.resultsUpload === true && renderResultsAction != null;

  // Interim: a bespoke-form step (fertilization, embryo development, freezing,
  // screening, PGT) shows a canonical row that opens StepDetailsModal instead
  // of rendering its inputs inline.
  const showDetailsRow =
    stepUsesDetailsModal(step.id) && renderDetailsAction != null;

  // Required-field gating, context-aware:
  // - Scheduling (future date): only date/datetime fields must be filled.
  //   Counts like eggsCollected don't exist yet — the procedure hasn't
  //   happened. Forcing them would block scheduling entirely.
  // - Completing (today or past date): all required fields must be filled.
  //   Numeric zero is a valid clinical result and must not be treated as
  //   missing (for example, a retrieval with zero oocytes).
  const missingRequiredField = useMemo(() => {
    for (const field of step.fields ?? []) {
      if (!field.required) continue;
      const isDateField = field.type === "datetime" || field.type === "date";
      // In planning mode (no date or future date) we only block on the
      // date itself. Counts like eggsCollected can't exist yet.
      if (isPlanningProcedure && !isDateField) continue;
      const value = localData[field.name];
      if (value === undefined || value === null || value === "") return field;
      if (field.type === "number") {
        const num = typeof value === "number" ? value : Number(value);
        if (!Number.isFinite(num) || num < 0) return field;
      }
    }
    return null;
  }, [step.fields, localData, isPlanningProcedure]);
  const canComplete = !missingRequiredField;
  const effectivePendingLabel = isPlanningProcedure
    ? t("care.treatment_flows.actions.schedule")
    : resolvedPendingActionLabel;
  const allMedications = linkedMedications;
  const deleteMedication = useDeleteMedication();
  const queryClient = useQueryClient();
  const { tenantId, patientId } = useApiClient();
  const [medMenuId, setMedMenuId] = useState<string | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState<string | null>(null);
  const [customMedConfirmed, setCustomMedConfirmed] = useState(false);
  const [customMedConfirmed2, setCustomMedConfirmed2] = useState(false);

  // Fetch medication catalog for dropdown fields
  const hasDropdownField = step.fields?.some((f) => f.type === "dropdown");
  const { data: dbMedications = [] } = useMedications({
    enabled: !!hasDropdownField,
  });

  const handleFieldChange = useCallback(
    (fieldName: string, value: string | number | null) => {
      setLocalData((prev) => ({ ...prev, [fieldName]: value }));
    },
    [],
  );

  // Status label
  const statusLabel = useMemo(() => {
    if (status === "completed") {
      return {
        text: t("care.treatment_flows.status.completed"),
        color: greenAccent,
        bg: "rgba(74, 222, 128, 0.1)",
      };
    }
    if (status === "skipped") {
      return {
        text: t("care.treatment_flows.status.skipped"),
        color: "#64748b",
        bg: "rgba(100, 116, 139, 0.1)",
      };
    }
    if (isCurrentStep) {
      return {
        text: t("care.treatment_flows.status.in_progress"),
        color: violetAccent,
        bg: "rgba(196, 181, 253, 0.1)",
      };
    }
    return null;
  }, [status, isCurrentStep, t]);

  return (
    <View style={{ gap: 20 }}>
      {/* Step Header — hidden when embedded inline in HubStepCard */}
      {!hideHeader && (
        <View>
          <View className="mb-1.5 flex-row items-center justify-between">
            <Text
              style={{
                fontSize: 18,
                fontWeight: "600",
                color: status === "completed" ? greenAccent : "#ffffff",
                letterSpacing: -0.3,
              }}
            >
              {step.title}
            </Text>
            {statusLabel && (
              <View
                style={{
                  paddingHorizontal: 10,
                  paddingVertical: 4,
                  borderRadius: 999,
                  backgroundColor: statusLabel.bg,
                }}
              >
                <Text
                  style={{
                    fontSize: 10,
                    fontWeight: "700",
                    color: statusLabel.color,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  {statusLabel.text}
                </Text>
              </View>
            )}
          </View>
          <Text style={{ fontSize: 12, color: c.descText, lineHeight: 18 }}>
            {step.description}
          </Text>
        </View>
      )}

      {/* Completed / Skipped state */}
      {(status === "completed" || status === "skipped") && (
        <View
          style={{
            padding: 16,
            borderRadius: 12,
            backgroundColor:
              status === "completed"
                ? "rgba(74, 222, 128, 0.05)"
                : "rgba(100, 116, 139, 0.05)",
            borderWidth: 1,
            borderColor:
              status === "completed"
                ? "rgba(74, 222, 128, 0.15)"
                : "rgba(100, 116, 139, 0.15)",
            alignItems: "center",
            gap: 12,
          }}
        >
          <View className="flex-row items-center gap-2">
            <Check
              size={16}
              color={status === "completed" ? greenAccent : "#64748b"}
              strokeWidth={3}
            />
            <Text
              style={{
                fontSize: 14,
                fontWeight: "500",
                color: status === "completed" ? greenAccent : "#64748b",
              }}
            >
              {status === "completed"
                ? t("care.treatment_flows.state.step_complete")
                : t("care.treatment_flows.state.step_skipped")}
            </Text>
          </View>

          {/* Show saved data summary for completed steps */}
          {status === "completed" && allMedications.length > 0 && (
            <View style={{ width: "100%", gap: renderMedicationCard ? 0 : 6 }}>
              {allMedications.slice(0, 4).map((med, i) =>
                renderMedicationCard ? (
                  <View
                    key={med.id}
                    style={{
                      borderTopWidth: i > 0 ? 1 : 0,
                      borderTopColor: "rgba(0,0,0,0.05)",
                    }}
                  >
                    {renderMedicationCard(med, { onMenu: setMedMenuId })}
                  </View>
                ) : (
                  <View
                    key={med.id}
                    className="flex-row items-center gap-2 py-1"
                  >
                    <Pill size={12} color={c.accent} />
                    <Text style={{ fontSize: 12, color: c.completedMedText }}>
                      {med.name}
                      {med.dose
                        ? ` - ${med.dose} ${med.doseUnit ?? ""}`.trim()
                        : ""}
                    </Text>
                  </View>
                ),
              )}
              {allMedications.length > 4 && (
                <Text style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                  {t("care.treatment_flows.medications.more_count", {
                    params: { count: String(allMedications.length - 4) },
                  })}
                </Text>
              )}
            </View>
          )}

          {/* Undo button */}
          <Pressable
            onPress={() => {
              capturePress(
                pressLabel(t("care.treatment_flows.actions.undo")),
                "care.treatment_flows.actions.undo",
              );
              onUndo();
            }}
            className="flex-row items-center gap-1.5 rounded-lg px-3 py-1.5"
            style={({ pressed }) => ({
              backgroundColor: pressed ? c.undoBgPressed : c.undoBg,
              opacity: pressed ? 0.7 : 1,
            })}
          >
            <RotateCcw size={12} color={c.undo} />
            <Text style={{ fontSize: 12, color: c.undo }}>
              {t("care.treatment_flows.actions.undo")}
            </Text>
          </Pressable>
        </View>
      )}

      {/* Pending state - show form content */}
      {status === "pending" && (
        <>
          {/* Prompt */}
          {hidePrompt ||
          showScheduleRow ||
          showScheduledConfirmation ||
          showOutcomeRow ||
          showResultsRow ||
          showDetailsRow ? null : isHubPresentation ? (
            <View style={{ gap: 5 }}>
              <Text
                style={{
                  fontSize: 14,
                  lineHeight: 18,
                  fontWeight: "700",
                  color: c.prompt,
                }}
              >
                {getHubPromptTitle(step, { isPlanning: isPlanningProcedure })}
              </Text>
              <Text
                style={{
                  fontSize: 12.5,
                  lineHeight: 18,
                  color: c.descText,
                }}
              >
                {getHubPromptSubtitle(step)}
              </Text>
            </View>
          ) : (
            <View
              style={{
                padding: 16,
                borderRadius: 12,
                backgroundColor: c.promptBg,
                borderWidth: 1,
                borderColor: c.promptBorder,
              }}
            >
              <Text
                style={{
                  fontSize: 14,
                  fontWeight: "500",
                  color: c.prompt,
                  lineHeight: 20,
                }}
              >
                {isPlanningProcedure && step.schedulingPrompt
                  ? step.schedulingPrompt
                  : (step.prompt ?? step.description)}
              </Text>
            </View>
          )}

          {/* Medications Content */}
          {step.contentType === "medications" && (
            <View>
              {allMedications.length > 0 ? (
                <View
                  style={{
                    gap: renderMedicationCard ? 0 : 10,
                    marginBottom: 16,
                  }}
                >
                  {allMedications.slice(0, 4).map((med, i) => {
                    if (renderMedicationCard) {
                      return (
                        <View
                          key={med.id}
                          style={{
                            borderTopWidth: i > 0 ? 1 : 0,
                            borderTopColor: "rgba(0,0,0,0.05)",
                          }}
                        >
                          {renderMedicationCard(med, {
                            onMenu: setMedMenuId,
                          })}
                        </View>
                      );
                    }
                    const isTriggerShot = med.medicationRole === "trigger_shot";
                    const medDate = med.startDate
                      ? formatMedicationDate(med.startDate)
                      : null;
                    return (
                      <View
                        key={med.id}
                        className="flex-row items-center gap-2.5 rounded-[10px] border p-3"
                        style={{
                          backgroundColor: isTriggerShot
                            ? "rgba(251, 191, 36, 0.08)"
                            : c.medBg,
                          borderColor: isTriggerShot
                            ? "rgba(251, 191, 36, 0.25)"
                            : c.medBorder,
                        }}
                      >
                        <View
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: 16,
                            backgroundColor: isTriggerShot
                              ? "rgba(251, 191, 36, 0.15)"
                              : "rgba(167, 139, 250, 0.1)",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {isTriggerShot ? (
                            <Zap size={14} color="#fbbf24" />
                          ) : (
                            <Pill size={14} color={c.accent} />
                          )}
                        </View>
                        <View style={{ flex: 1 }}>
                          <View className="flex-row items-center gap-1.5">
                            <Text
                              style={{
                                fontSize: 13,
                                fontWeight: "500",
                                color: c.medName,
                                flex: 1,
                              }}
                            >
                              {med.name ||
                                t("care.treatment_flows.medications.unnamed")}
                            </Text>
                            {isTriggerShot && (
                              <View
                                style={{
                                  paddingHorizontal: 6,
                                  paddingVertical: 2,
                                  borderRadius: 4,
                                  backgroundColor: "rgba(251, 191, 36, 0.2)",
                                }}
                              >
                                <Text
                                  style={{
                                    fontSize: 9,
                                    fontWeight: "700",
                                    color: "#fbbf24",
                                    textTransform: "uppercase",
                                    letterSpacing: 0.3,
                                  }}
                                >
                                  {t(
                                    "care.treatment_flows.medications.trigger_badge",
                                  )}
                                </Text>
                              </View>
                            )}
                          </View>
                          <View className="mt-0.5 flex-row items-center justify-between">
                            <Text style={{ fontSize: 11, color: c.medDose }}>
                              {med.dose
                                ? `${med.dose} ${med.doseUnit ?? ""}`.trim()
                                : ""}
                            </Text>
                            {medDate && (
                              <Text style={{ fontSize: 10, color: c.medDate }}>
                                {medDate}
                              </Text>
                            )}
                          </View>
                        </View>
                        <Pressable
                          onPress={() => {
                            capturePress("medication_menu");
                            setMedMenuId(med.id);
                          }}
                          hitSlop={8}
                          style={{ padding: 4 }}
                        >
                          <MoreVertical size={16} color={c.medDate} />
                        </Pressable>
                      </View>
                    );
                  })}
                  {allMedications.length > 4 && (
                    <Text
                      style={{
                        fontSize: 11,
                        color: "#64748b",
                        marginTop: 4,
                      }}
                    >
                      {t("care.treatment_flows.medications.more_count", {
                        params: {
                          count: String(allMedications.length - 4),
                        },
                      })}
                    </Text>
                  )}
                </View>
              ) : renderMedicationEmptyState ? (
                <View
                  style={{ marginBottom: hidePendingCompleteAction ? 0 : 16 }}
                >
                  {renderMedicationEmptyState({ onAddMedication })}
                </View>
              ) : (
                <View
                  style={{
                    padding: 16,
                    borderRadius: 10,
                    borderWidth: 1,
                    borderStyle: "dashed",
                    borderColor: c.noMedBorder,
                    alignItems: "center",
                    marginBottom: 16,
                  }}
                >
                  <Pill
                    size={20}
                    color={c.noMedText}
                    style={{ marginBottom: 8 }}
                  />
                  <Text
                    style={{
                      fontSize: 12,
                      color: c.noMedText,
                      textAlign: "center",
                    }}
                  >
                    {t("care.treatment_flows.medications.none_logged")}
                  </Text>
                </View>
              )}

              {/* Medication action menu */}
              <Modal
                visible={medMenuId !== null}
                transparent
                animationType="fade"
                onRequestClose={() => setMedMenuId(null)}
                statusBarTranslucent
              >
                <Pressable
                  style={{
                    flex: 1,
                    backgroundColor: "rgba(0,0,0,0.6)",
                    justifyContent: "flex-end",
                  }}
                  onPress={() => setMedMenuId(null)}
                >
                  <View
                    style={{
                      backgroundColor: c.menuBg,
                      borderTopLeftRadius: 20,
                      borderTopRightRadius: 20,
                      borderWidth: 1,
                      borderColor: c.menuBorder,
                      padding: 24,
                      gap: 12,
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 13,
                        color: c.menuText,
                        marginBottom: 4,
                      }}
                    >
                      {allMedications.find((m) => m.id === medMenuId)?.name ??
                        t("care.treatment_flows.medications.medication")}
                    </Text>
                    <Pressable
                      onPress={() => {
                        capturePress(
                          pressLabel(
                            t("care.treatment_flows.actions.delete_medication"),
                          ),
                          "care.treatment_flows.actions.delete_medication",
                        );
                        const id = medMenuId;
                        setMedMenuId(null);
                        if (!id) return;
                        deleteMedication.mutate(id, {
                          onSuccess: () => {
                            queryClient.invalidateQueries({
                              queryKey: ["medications", tenantId, patientId],
                            });
                          },
                        });
                      }}
                      className="flex-row items-center gap-3 rounded-xl border p-3.5"
                      style={({ pressed }) => ({
                        backgroundColor: pressed
                          ? "rgba(244,63,94,0.15)"
                          : "rgba(244,63,94,0.08)",
                        borderColor: "rgba(244,63,94,0.2)",
                      })}
                    >
                      <Trash2 size={16} color="#f43f5e" />
                      <Text
                        style={{
                          fontSize: 14,
                          color: "#f43f5e",
                          fontWeight: "500",
                        }}
                      >
                        {t("care.treatment_flows.actions.delete_medication")}
                      </Text>
                    </Pressable>
                    <Pressable
                      onPress={() => {
                        capturePress(
                          pressLabel(t("care.treatment_flows.actions.cancel")),
                          "care.treatment_flows.actions.cancel",
                        );
                        setMedMenuId(null);
                      }}
                      style={{ alignItems: "center", paddingVertical: 12 }}
                    >
                      <Text style={{ fontSize: 14, color: c.menuCancel }}>
                        {t("care.treatment_flows.actions.cancel")}
                      </Text>
                    </Pressable>
                  </View>
                </Pressable>
              </Modal>

              {/* Add medication buttons */}
              <View style={{ gap: 10 }}>
                {isHubPresentation ? (
                  allMedications.length > 0 ? (
                    <CarePillActionButton
                      label={t(
                        "care.treatment_flows.actions.add_another_medication",
                      )}
                      onPress={onAddMedication}
                      icon={Plus}
                      variant="secondary"
                    />
                  ) : null
                ) : (
                  <Pressable
                    onPress={() => {
                      capturePress("add_medication");
                      onAddMedication();
                    }}
                    style={({ pressed }) => ({
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                      paddingVertical: 12,
                      borderRadius: 10,
                      backgroundColor: pressed ? `${c.accent}20` : c.accentBg,
                      borderWidth: 1,
                      borderColor: c.accentBorder,
                    })}
                  >
                    <Plus size={16} color={c.accent} />
                    <Text
                      style={{
                        fontSize: 13,
                        color: c.accent,
                        fontWeight: "600",
                      }}
                    >
                      {allMedications.length > 0
                        ? t(
                            "care.treatment_flows.actions.add_another_medication",
                          )
                        : t("care.treatment_flows.actions.add_medication")}
                    </Text>
                    <ExternalLink
                      size={12}
                      color={c.accent}
                      style={{ marginLeft: 2 }}
                    />
                  </Pressable>
                )}

                {step.hasTriggerShot &&
                  onAddTriggerShot &&
                  (() => {
                    const hasTriggerMed = allMedications.some(
                      (m) => m.medicationRole === "trigger_shot",
                    );
                    if (isHubPresentation) {
                      return (
                        <CarePillActionButton
                          label={
                            hasTriggerMed
                              ? t("care.treatment_flows.actions.trigger_added")
                              : t("care.treatment_flows.actions.add_trigger")
                          }
                          onPress={onAddTriggerShot}
                          icon={Zap}
                          variant="secondary"
                          disabled={hasTriggerMed}
                        />
                      );
                    }
                    return (
                      <Pressable
                        onPress={() => {
                          capturePress("add_trigger_shot");
                          onAddTriggerShot();
                        }}
                        disabled={hasTriggerMed}
                        accessibilityRole="button"
                        accessibilityLabel={t(
                          "care.treatment_flows.actions.add_trigger_a11y",
                        )}
                        style={({ pressed }) => ({
                          flexDirection: "row",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 8,
                          paddingVertical: 12,
                          borderRadius: 10,
                          backgroundColor: hasTriggerMed
                            ? "rgba(251, 191, 36, 0.05)"
                            : pressed
                              ? "rgba(251, 191, 36, 0.2)"
                              : "rgba(251, 191, 36, 0.1)",
                          borderWidth: 1,
                          borderColor: hasTriggerMed
                            ? "rgba(251, 191, 36, 0.15)"
                            : "rgba(251, 191, 36, 0.3)",
                          opacity: hasTriggerMed ? 0.5 : 1,
                        })}
                      >
                        <Zap size={16} color="#fbbf24" />
                        <Text
                          style={{
                            fontSize: 13,
                            color: "#fbbf24",
                            fontWeight: "600",
                          }}
                        >
                          {hasTriggerMed
                            ? t("care.treatment_flows.actions.trigger_added")
                            : t("care.treatment_flows.actions.add_trigger")}
                        </Text>
                        {!hasTriggerMed && (
                          <ExternalLink
                            size={12}
                            color="#fbbf24"
                            style={{ marginLeft: 2 }}
                          />
                        )}
                      </Pressable>
                    );
                  })()}
              </View>
            </View>
          )}

          {/* Custom Fields — or, for an unscheduled procedure, the canonical
              row that hands off to the normal appointment flow. */}
          {/* A dated procedure whose date has passed said so nowhere. The future
              case has had its confirmation line since #3273; a past one fell
              through to the fields, so the one thing the patient had just
              recorded was legible only inside an input. Rendered beside the chain
              below rather than inside it, so it displaces nothing — the retrieval
              outcome row and the fields both still get their turn.

              "Dated", not "happened on". `deriveStepProgress` back-fills the
              step's date field from `phase.startDate` when the record carries
              none, so saving a collection with no date at all leaves today's date
              in that field — and a line reading "Happened on 26 Aug" would be
              asserting an event on a date she never gave. Naming the date without
              claiming the event is true either way. */}
          {showCollectedConfirmation ? (
            <View style={{ marginTop: 4 }}>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  lineHeight: 18,
                  fontWeight: "600",
                  color: c.label,
                }}
              >
                {t("care.treatment_flows.dated_on", {
                  params: {
                    date: formatDate(procedureDate.dateIso ?? "", locale),
                  },
                })}
              </Text>
            </View>
          ) : null}
          {showScheduleRow ? (
            <View style={{ marginTop: 4 }}>{renderScheduleAction()}</View>
          ) : showScheduledConfirmation ? (
            <View style={{ marginTop: 4 }}>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  lineHeight: 18,
                  fontWeight: "600",
                  color: c.label,
                }}
              >
                {t("care.treatment_flows.scheduled_for_date", {
                  params: {
                    date: formatDate(procedureDate.dateIso ?? "", locale),
                  },
                })}
              </Text>
            </View>
          ) : showOutcomeRow ? (
            <View style={{ marginTop: 4 }}>{renderOutcomeAction()}</View>
          ) : showResultsRow ? (
            <View style={{ marginTop: 4 }}>{renderResultsAction()}</View>
          ) : showDetailsRow ? (
            <View style={{ marginTop: 4 }}>{renderDetailsAction()}</View>
          ) : step.fields && step.fields.length > 0 ? (
            <View
              style={{ marginTop: step.contentType === "medications" ? 4 : 0 }}
            >
              {step.fields
                .filter(
                  (field) =>
                    (!field.showIf ||
                      stepData[field.showIf.field] === field.showIf.equals) &&
                    // While scheduling a procedure (no date / future date) only
                    // the date field is relevant — the outcome fields (counts,
                    // notes) describe what happened, which hasn't yet.
                    (!isPlanningProcedure ||
                      field.type === "datetime" ||
                      field.type === "date"),
                )
                .map((field) => (
                  <View key={field.name} style={{ marginBottom: 12 }}>
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 13,
                        fontWeight: "500",
                        color: c.label,
                        marginBottom: 6,
                      }}
                    >
                      {field.label}
                    </Text>
                    {field.type === "select" && field.options ? (
                      <View className="flex-row flex-wrap gap-2">
                        {field.options.map((opt) => {
                          const isSelected =
                            (stepData[field.name] as string) === opt;
                          return (
                            <Pressable
                              key={opt}
                              onPress={() => {
                                capturePress("step_field_option");
                                handleFieldChange(field.name, opt);
                              }}
                              style={{
                                paddingHorizontal: 16,
                                paddingVertical: 10,
                                borderRadius: 12,
                                backgroundColor: isSelected
                                  ? c.selectedBg
                                  : c.fieldBg,
                                borderWidth: 1,
                                borderColor: isSelected
                                  ? c.selectedBorder
                                  : c.fieldBorder,
                              }}
                            >
                              <Text
                                className="font-dm-sans"
                                style={{
                                  fontSize: 14,
                                  color: isSelected ? c.fieldSelected : c.label,
                                  fontWeight: isSelected ? "700" : "500",
                                  textTransform: "capitalize",
                                }}
                              >
                                {tf(
                                  t,
                                  `care.treatment_flows.options.${opt}`,
                                  opt,
                                )}
                              </Text>
                            </Pressable>
                          );
                        })}
                      </View>
                    ) : field.type === "dropdown" ? (
                      (() => {
                        const options = [
                          ...(field.medicationTypeFilter
                            ? dbMedications
                                .filter(
                                  (m) =>
                                    m.name &&
                                    (m.type ?? 0) &
                                      (field.medicationTypeFilter ?? 0),
                                )
                                .map((m) => m.name!)
                            : (field.options ?? [])),
                          ...(field.medicationTypeFilter ? ["Other"] : []),
                        ];
                        const currentValue =
                          (stepData[field.name] as string) ?? "";
                        const isOpen = dropdownOpen === field.name;
                        // Display label transformer — collection-method values
                        // are stored lowercase to match the API enum but should
                        // render with clinical capitalization.
                        const formatLabel = (opt: string): string => {
                          if (field.name === "collectionMethod") {
                            return tf(
                              t,
                              `care.treatment_flows.collection_methods.${opt}`,
                              opt,
                            );
                          }
                          return tf(
                            t,
                            `care.treatment_flows.options.${opt}`,
                            opt,
                          );
                        };
                        return (
                          <View>
                            <Pressable
                              onPress={() => {
                                capturePress("step_field_dropdown");
                                setDropdownOpen(isOpen ? null : field.name);
                              }}
                              className="flex-row items-center justify-between rounded-lg border px-3 py-2.5"
                              style={{
                                backgroundColor: c.fieldBg,
                                borderColor: isOpen
                                  ? c.selectedBorder
                                  : c.fieldBorder,
                              }}
                            >
                              <Text
                                style={{
                                  fontSize: 13,
                                  color: currentValue
                                    ? c.fieldText
                                    : c.fieldPlaceholder,
                                }}
                              >
                                {currentValue === "Other"
                                  ? (stepData[
                                      `${field.name}Custom`
                                    ] as string) ||
                                    (field.name === "triggerMedication"
                                      ? (stepData.triggerMedicationCustom as string) ||
                                        t(
                                          "care.treatment_flows.options.other_enter_below",
                                        )
                                      : t(
                                          "care.treatment_flows.options.other_enter_below",
                                        ))
                                  : currentValue
                                    ? formatLabel(currentValue)
                                    : field.placeholder ||
                                      t(
                                        "care.treatment_flows.options.select_placeholder",
                                      )}
                              </Text>
                              <ChevronDown size={14} color={c.label} />
                            </Pressable>
                            {isOpen && (
                              <View
                                style={{
                                  marginTop: 4,
                                  backgroundColor: c.dropdownBg,
                                  borderWidth: 1,
                                  borderColor: c.dropdownBorder,
                                  borderRadius: 8,
                                  maxHeight: 200,
                                  overflow: "hidden",
                                }}
                              >
                                <ScrollView nestedScrollEnabled>
                                  {options.map((opt) => {
                                    const isSelected = currentValue === opt;
                                    return (
                                      <Pressable
                                        key={opt}
                                        onPress={() => {
                                          capturePress("step_field_option");
                                          handleFieldChange(field.name, opt);
                                          setDropdownOpen(null);
                                          if (
                                            field.name === "triggerMedication"
                                          ) {
                                            if (opt !== "Other") {
                                              handleFieldChange(
                                                "triggerDose",
                                                TRIGGER_DEFAULT_DOSES[opt] ??
                                                  "",
                                              );
                                            } else {
                                              handleFieldChange(
                                                "triggerDose",
                                                "",
                                              );
                                              setCustomMedConfirmed(false);
                                            }
                                          }
                                        }}
                                        className={
                                          opt === "Other"
                                            ? "m-2 flex-row items-center gap-3 rounded-xl border p-3"
                                            : "px-3 py-2.5"
                                        }
                                        style={
                                          opt === "Other"
                                            ? {
                                                borderStyle: "dashed",
                                                borderColor: c.otherBorder,
                                              }
                                            : {
                                                backgroundColor: isSelected
                                                  ? `${c.fieldSelected}18`
                                                  : "transparent",
                                                borderBottomWidth: 1,
                                                borderBottomColor:
                                                  c.dropdownSep,
                                              }
                                        }
                                      >
                                        {opt === "Other" ? (
                                          <>
                                            <View
                                              style={{
                                                width: 32,
                                                height: 32,
                                                borderRadius: 16,
                                                backgroundColor: c.otherBg,
                                                alignItems: "center",
                                                justifyContent: "center",
                                              }}
                                            >
                                              <Plus
                                                size={16}
                                                color={c.otherIcon}
                                              />
                                            </View>
                                            <Text
                                              style={{
                                                fontSize: 13,
                                                fontWeight: "500",
                                                color: c.otherText,
                                              }}
                                            >
                                              {t(
                                                "care.treatment_flows.options.other",
                                              )}
                                            </Text>
                                          </>
                                        ) : (
                                          <Text
                                            style={{
                                              fontSize: 13,
                                              color: isSelected
                                                ? c.fieldSelected
                                                : c.dropdownItem,
                                              fontWeight: isSelected
                                                ? "600"
                                                : "400",
                                            }}
                                          >
                                            {formatLabel(opt)}
                                          </Text>
                                        )}
                                      </Pressable>
                                    );
                                  })}
                                  {options.length === 0 && (
                                    <Text
                                      style={{
                                        padding: 12,
                                        fontSize: 13,
                                        color: c.noMedText,
                                      }}
                                    >
                                      {t(
                                        "care.treatment_flows.medications.none_found",
                                      )}
                                    </Text>
                                  )}
                                </ScrollView>
                              </View>
                            )}
                            {currentValue === "Other" &&
                              field.name === "triggerMedication" &&
                              !customMedConfirmed && (
                                <View
                                  style={{
                                    marginTop: 8,
                                    borderRadius: 12,
                                    borderWidth: 1,
                                    borderColor: c.customBorder,
                                    backgroundColor: c.customBg,
                                    overflow: "hidden",
                                  }}
                                >
                                  <View
                                    style={{
                                      borderBottomWidth: 1,
                                      borderBottomColor: c.dropdownSep,
                                      paddingHorizontal: 16,
                                      paddingVertical: 10,
                                    }}
                                  >
                                    <Text
                                      style={{
                                        fontSize: 12,
                                        fontWeight: "600",
                                        color: c.customAccent,
                                      }}
                                    >
                                      Add custom medication
                                    </Text>
                                  </View>
                                  <View style={{ padding: 16 }}>
                                    <TextInput
                                      value={
                                        (stepData.triggerMedicationCustom as string) ??
                                        ""
                                      }
                                      onChangeText={(text) =>
                                        handleFieldChange(
                                          "triggerMedicationCustom",
                                          text,
                                        )
                                      }
                                      placeholder="Enter medication name..."
                                      placeholderTextColor={c.fieldPlaceholder}
                                      autoFocus
                                      style={{
                                        width: "100%",
                                        borderRadius: 12,
                                        borderWidth: 1,
                                        borderColor: c.customInputBorder,
                                        backgroundColor: c.customInputBg,
                                        paddingHorizontal: 16,
                                        paddingVertical: 12,
                                        fontSize: 13,
                                        color: c.customInputText,
                                      }}
                                    />
                                    <View className="mt-3 flex-row gap-2">
                                      <Pressable
                                        onPress={() => {
                                          capturePress(
                                            "custom_medication_cancel",
                                          );
                                          handleFieldChange(
                                            "triggerMedication",
                                            "",
                                          );
                                          handleFieldChange(
                                            "triggerMedicationCustom",
                                            "",
                                          );
                                          setCustomMedConfirmed(false);
                                        }}
                                        style={{
                                          flex: 1,
                                          alignItems: "center",
                                          borderRadius: 8,
                                          borderWidth: 1,
                                          borderColor: c.cancelBorder,
                                          paddingVertical: 10,
                                        }}
                                      >
                                        <Text
                                          style={{
                                            fontSize: 13,
                                            fontWeight: "500",
                                            color: c.cancelText,
                                          }}
                                        >
                                          Cancel
                                        </Text>
                                      </Pressable>
                                      <Pressable
                                        onPress={() => {
                                          capturePress("custom_medication_add");
                                          setCustomMedConfirmed(true);
                                        }}
                                        style={{
                                          flex: 1,
                                          alignItems: "center",
                                          borderRadius: 8,
                                          backgroundColor: c.customAccent,
                                          paddingVertical: 10,
                                        }}
                                      >
                                        <Text
                                          style={{
                                            fontSize: 13,
                                            fontWeight: "500",
                                            color: "#020617",
                                          }}
                                        >
                                          Add
                                        </Text>
                                      </Pressable>
                                    </View>
                                  </View>
                                </View>
                              )}
                            {currentValue === "Other" &&
                              field.name !== "triggerMedication" && (
                                <TextInput
                                  value={
                                    (stepData[
                                      `${field.name}Custom`
                                    ] as string) ?? ""
                                  }
                                  onChangeText={(text) =>
                                    handleFieldChange(
                                      `${field.name}Custom`,
                                      text,
                                    )
                                  }
                                  placeholder={`Enter ${field.label.toLowerCase()}...`}
                                  placeholderTextColor={c.fieldPlaceholder}
                                  autoFocus
                                  style={{
                                    marginTop: 8,
                                    width: "100%",
                                    borderRadius: 8,
                                    borderWidth: 1,
                                    borderColor: c.fieldBorder,
                                    backgroundColor: c.fieldBg,
                                    paddingHorizontal: 12,
                                    paddingVertical: 10,
                                    fontSize: 13,
                                    color: c.fieldText,
                                  }}
                                />
                              )}
                          </View>
                        );
                      })()
                    ) : field.type === "textarea" ? (
                      <MoonFieldInput paddingX={12} paddingY={0}>
                        <TextInput
                          value={(stepData[field.name] as string) ?? ""}
                          onChangeText={(text) =>
                            handleFieldChange(field.name, text)
                          }
                          placeholder={field.placeholder}
                          placeholderTextColor={EMBER.colors.textFaint}
                          multiline
                          className="font-dm-sans"
                          style={{
                            minHeight: 88,
                            paddingTop: 11,
                            paddingBottom: 11,
                            fontSize: 15,
                            lineHeight: 21,
                            color: EMBER.colors.text,
                            textAlignVertical: "top",
                          }}
                        />
                      </MoonFieldInput>
                    ) : field.type === "datetime" ? (
                      renderDateTimePicker ? (
                        renderDateTimePicker({
                          value: (stepData[field.name] as string) ?? null,
                          onChange: (isoString) =>
                            handleFieldChange(field.name, isoString),
                          placeholder: field.placeholder,
                        })
                      ) : (
                        <DateTimeFieldPicker
                          value={(stepData[field.name] as string) ?? null}
                          onChange={(isoString) =>
                            handleFieldChange(field.name, isoString)
                          }
                          placeholder={field.placeholder}
                        />
                      )
                    ) : field.type === "upload" ? (
                      <TreatmentUploadField
                        value={(stepData[field.name] as string) ?? null}
                        onChange={(docId, uploadId) => {
                          handleFieldChange(field.name, docId);
                          if (step.id === "pgt_testing") {
                            handleFieldChange("pgtResultUploadId", uploadId);
                          }
                        }}
                        label={`Upload ${field.label}`}
                        tags={["medical_records", `step:${step.id}`]}
                        bg={c.fieldBg}
                        border={c.fieldBorder}
                        text={c.fieldText}
                      />
                    ) : field.type === "dose" ? (
                      <DoseField
                        value={String(stepData[field.name] ?? "")}
                        onChange={(next) => handleFieldChange(field.name, next)}
                        placeholder={field.placeholder}
                        dropdownKey={field.name}
                        dropdownOpen={dropdownOpen}
                        setDropdownOpen={setDropdownOpen}
                        colors={{
                          fieldBg: c.fieldBg,
                          fieldBorder: c.fieldBorder,
                          fieldText: c.fieldText,
                          fieldPlaceholder: c.fieldPlaceholder,
                          selectedBorder: c.selectedBorder,
                          label: c.label,
                          dropdownBg: c.dropdownBg,
                          dropdownBorder: c.dropdownBorder,
                          dropdownItem: c.dropdownItem,
                          dropdownSep: c.dropdownSep,
                          fieldSelected: c.fieldSelected,
                        }}
                      />
                    ) : (
                      <MoonFieldInput
                        paddingX={14}
                        paddingY={0}
                        style={{ flexDirection: "row", alignItems: "center" }}
                      >
                        <TextInput
                          value={String(stepData[field.name] ?? "")}
                          onChangeText={(text) =>
                            handleFieldChange(
                              field.name,
                              field.type === "number"
                                ? text
                                  ? Number(text)
                                  : null
                                : text,
                            )
                          }
                          placeholder={field.placeholder}
                          placeholderTextColor={EMBER.colors.textFaint}
                          keyboardType={
                            field.type === "number" ? "numeric" : "default"
                          }
                          className="font-dm-sans"
                          style={{
                            flex: 1,
                            paddingVertical: 14,
                            fontSize: 15,
                            color: EMBER.colors.text,
                          }}
                        />
                      </MoonFieldInput>
                    )}
                  </View>
                ))}

              {/* Second Trigger Medication */}
              {step.id === "trigger_shot" &&
                !!(stepData.triggerMedication as string) &&
                (stepData.triggerMedication as string) !== "__pending__" && (
                  <>
                    {!(stepData.triggerMedication2 as string) ? (
                      <Pressable
                        onPress={() => {
                          capturePress("add_second_medication");
                          handleFieldChange(
                            "triggerMedication2",
                            "__pending__",
                          );
                        }}
                        className="mt-1 flex-row items-center gap-3 rounded-xl border p-3"
                        style={{
                          borderStyle: "dashed",
                          borderColor: c.otherBorder,
                        }}
                      >
                        <View
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: 16,
                            backgroundColor: c.otherBg,
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Plus size={16} color={c.otherIcon} />
                        </View>
                        <Text
                          style={{
                            fontSize: 13,
                            fontWeight: "500",
                            color: c.otherText,
                          }}
                        >
                          Add second medication
                        </Text>
                      </Pressable>
                    ) : (
                      <View style={{ marginTop: 4 }}>
                        {/* Header row */}
                        <View className="mb-1.5 flex-row items-center justify-between">
                          <Text
                            style={{
                              fontSize: 12,
                              fontWeight: "500",
                              color: c.secondMedLabel,
                            }}
                          >
                            Second Medication
                          </Text>
                          <Pressable
                            onPress={() => {
                              capturePress("remove_second_medication");
                              handleFieldChange("triggerMedication2", "");
                              handleFieldChange("triggerDose2", "");
                              handleFieldChange("triggerMedicationCustom2", "");
                              setCustomMedConfirmed2(false);
                            }}
                          >
                            <X size={14} color={c.medDate} />
                          </Pressable>
                        </View>

                        {/* Second medication dropdown */}
                        {(() => {
                          const options2 = [
                            ...dbMedications
                              .filter((m) => m.name && (m.type ?? 0) & 8)
                              .map((m) => m.name!),
                            "Other",
                          ];
                          const currentValue2 =
                            (stepData.triggerMedication2 as string) ===
                            "__pending__"
                              ? ""
                              : ((stepData.triggerMedication2 as string) ?? "");
                          const isOpen2 = dropdownOpen === "triggerMedication2";
                          return (
                            <View style={{ marginBottom: 12 }}>
                              <Text
                                style={{
                                  fontSize: 10,
                                  fontWeight: "600",
                                  color: c.label,
                                  textTransform: "uppercase",
                                  letterSpacing: 0.5,
                                  marginBottom: 6,
                                }}
                              >
                                Trigger Medication
                              </Text>
                              <Pressable
                                onPress={() => {
                                  capturePress("step_field_dropdown");
                                  setDropdownOpen(
                                    isOpen2 ? null : "triggerMedication2",
                                  );
                                }}
                                className="flex-row items-center justify-between rounded-lg border px-3 py-2.5"
                                style={{
                                  backgroundColor: c.fieldBg,
                                  borderColor: isOpen2
                                    ? c.selectedBorder
                                    : c.fieldBorder,
                                }}
                              >
                                <Text
                                  style={{
                                    fontSize: 13,
                                    color: currentValue2
                                      ? c.fieldText
                                      : c.fieldPlaceholder,
                                  }}
                                >
                                  {currentValue2 === "Other"
                                    ? (stepData.triggerMedicationCustom2 as string) ||
                                      "Other (enter below)"
                                    : currentValue2 ||
                                      "Select trigger medication"}
                                </Text>
                                <ChevronDown size={14} color={c.label} />
                              </Pressable>
                              {isOpen2 && (
                                <View
                                  style={{
                                    marginTop: 4,
                                    backgroundColor: c.dropdownBg,
                                    borderWidth: 1,
                                    borderColor: c.dropdownBorder,
                                    borderRadius: 8,
                                    maxHeight: 200,
                                    overflow: "hidden",
                                  }}
                                >
                                  <ScrollView nestedScrollEnabled>
                                    {options2.map((opt) => {
                                      const isSelected = currentValue2 === opt;
                                      return (
                                        <Pressable
                                          key={opt}
                                          onPress={() => {
                                            capturePress("step_field_option");
                                            handleFieldChange(
                                              "triggerMedication2",
                                              opt,
                                            );
                                            setDropdownOpen(null);
                                            if (opt !== "Other") {
                                              handleFieldChange(
                                                "triggerDose2",
                                                TRIGGER_DEFAULT_DOSES[opt] ??
                                                  "",
                                              );
                                            } else {
                                              handleFieldChange(
                                                "triggerDose2",
                                                "",
                                              );
                                              setCustomMedConfirmed2(false);
                                            }
                                          }}
                                          className={
                                            opt === "Other"
                                              ? "m-2 flex-row items-center gap-3 rounded-xl border p-3"
                                              : "px-3 py-2.5"
                                          }
                                          style={
                                            opt === "Other"
                                              ? {
                                                  borderStyle: "dashed",
                                                  borderColor: c.otherBorder,
                                                }
                                              : {
                                                  backgroundColor: isSelected
                                                    ? `${c.fieldSelected}18`
                                                    : "transparent",
                                                  borderBottomWidth: 1,
                                                  borderBottomColor:
                                                    c.dropdownSep,
                                                }
                                          }
                                        >
                                          {opt === "Other" ? (
                                            <>
                                              <View
                                                style={{
                                                  width: 32,
                                                  height: 32,
                                                  borderRadius: 16,
                                                  backgroundColor: c.otherBg,
                                                  alignItems: "center",
                                                  justifyContent: "center",
                                                }}
                                              >
                                                <Plus
                                                  size={16}
                                                  color={c.otherIcon}
                                                />
                                              </View>
                                              <Text
                                                style={{
                                                  fontSize: 13,
                                                  fontWeight: "500",
                                                  color: c.otherText,
                                                }}
                                              >
                                                Other
                                              </Text>
                                            </>
                                          ) : (
                                            <Text
                                              style={{
                                                fontSize: 13,
                                                color: isSelected
                                                  ? c.fieldSelected
                                                  : c.dropdownItem,
                                                fontWeight: isSelected
                                                  ? "600"
                                                  : "400",
                                              }}
                                            >
                                              {opt}
                                            </Text>
                                          )}
                                        </Pressable>
                                      );
                                    })}
                                    {options2.length === 0 && (
                                      <Text
                                        style={{
                                          padding: 12,
                                          fontSize: 13,
                                          color: c.noMedText,
                                        }}
                                      >
                                        No medications found
                                      </Text>
                                    )}
                                  </ScrollView>
                                </View>
                              )}
                              {currentValue2 === "Other" &&
                                !customMedConfirmed2 && (
                                  <View
                                    style={{
                                      marginTop: 8,
                                      borderRadius: 12,
                                      borderWidth: 1,
                                      borderColor: c.customBorder,
                                      backgroundColor: c.customBg,
                                      overflow: "hidden",
                                    }}
                                  >
                                    <View
                                      style={{
                                        borderBottomWidth: 1,
                                        borderBottomColor: c.dropdownSep,
                                        paddingHorizontal: 16,
                                        paddingVertical: 10,
                                      }}
                                    >
                                      <Text
                                        style={{
                                          fontSize: 12,
                                          fontWeight: "600",
                                          color: c.customAccent,
                                        }}
                                      >
                                        Add custom medication
                                      </Text>
                                    </View>
                                    <View style={{ padding: 16 }}>
                                      <TextInput
                                        value={
                                          (stepData.triggerMedicationCustom2 as string) ??
                                          ""
                                        }
                                        onChangeText={(text) =>
                                          handleFieldChange(
                                            "triggerMedicationCustom2",
                                            text,
                                          )
                                        }
                                        placeholder="Enter medication name..."
                                        placeholderTextColor={
                                          c.fieldPlaceholder
                                        }
                                        autoFocus
                                        style={{
                                          width: "100%",
                                          borderRadius: 12,
                                          borderWidth: 1,
                                          borderColor: c.customInputBorder,
                                          backgroundColor: c.customInputBg,
                                          paddingHorizontal: 16,
                                          paddingVertical: 12,
                                          fontSize: 13,
                                          color: c.customInputText,
                                        }}
                                      />
                                      <View className="mt-3 flex-row gap-2">
                                        <Pressable
                                          onPress={() => {
                                            capturePress(
                                              "custom_medication_cancel",
                                            );
                                            handleFieldChange(
                                              "triggerMedication2",
                                              "",
                                            );
                                            handleFieldChange(
                                              "triggerMedicationCustom2",
                                              "",
                                            );
                                            setCustomMedConfirmed2(false);
                                          }}
                                          style={{
                                            flex: 1,
                                            alignItems: "center",
                                            borderRadius: 8,
                                            borderWidth: 1,
                                            borderColor: c.cancelBorder,
                                            paddingVertical: 10,
                                          }}
                                        >
                                          <Text
                                            style={{
                                              fontSize: 13,
                                              fontWeight: "500",
                                              color: c.cancelText,
                                            }}
                                          >
                                            Cancel
                                          </Text>
                                        </Pressable>
                                        <Pressable
                                          onPress={() => {
                                            capturePress(
                                              "custom_medication_add",
                                            );
                                            setCustomMedConfirmed2(true);
                                          }}
                                          style={{
                                            flex: 1,
                                            alignItems: "center",
                                            borderRadius: 8,
                                            backgroundColor: c.customAccent,
                                            paddingVertical: 10,
                                          }}
                                        >
                                          <Text
                                            style={{
                                              fontSize: 13,
                                              fontWeight: "500",
                                              color: "#020617",
                                            }}
                                          >
                                            Add
                                          </Text>
                                        </Pressable>
                                      </View>
                                    </View>
                                  </View>
                                )}
                            </View>
                          );
                        })()}

                        {/* Second dose — amount + unit picker */}
                        <View>
                          <Text
                            style={{
                              fontSize: 10,
                              fontWeight: "600",
                              color: c.label,
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                              marginBottom: 6,
                            }}
                          >
                            Dose
                          </Text>
                          <DoseField
                            value={String(stepData.triggerDose2 ?? "")}
                            onChange={(next) =>
                              handleFieldChange("triggerDose2", next)
                            }
                            placeholder="e.g. 250"
                            dropdownKey="triggerDose2"
                            dropdownOpen={dropdownOpen}
                            setDropdownOpen={setDropdownOpen}
                            colors={{
                              fieldBg: c.fieldBg,
                              fieldBorder: c.fieldBorder,
                              fieldText: c.fieldText,
                              fieldPlaceholder: c.fieldPlaceholder,
                              selectedBorder: c.selectedBorder,
                              label: c.label,
                              dropdownBg: c.dropdownBg,
                              dropdownBorder: c.dropdownBorder,
                              dropdownItem: c.dropdownItem,
                              dropdownSep: c.dropdownSep,
                              fieldSelected: c.fieldSelected,
                            }}
                          />
                        </View>
                      </View>
                    )}
                  </>
                )}
            </View>
          ) : null}

          {/* A record attached to this procedure, not an answer about it.
              It used to render above the fields, which put a navigation row
              between "How did your IUI go?" and the first thing being asked —
              cutting the form in two. After the fields it reads as what it is:
              one more thing you can attach, once you have said what happened. */}
          {renderLinkageAction ? (
            <View style={{ marginTop: 4 }}>{renderLinkageAction()}</View>
          ) : null}

          {/* Action Buttons */}
          <View style={{ gap: 12 }}>
            {/* Primary CTA — in hub mode use the warm pill so the action
                matches the rest of the Treatment Hub design; the green
                Pressable is kept only for legacy non-hub callers. When the
                canonical schedule row is shown, the row is the sole action. */}
            {!hidePendingCompleteAction &&
              !showScheduleRow &&
              !showOutcomeRow &&
              !showResultsRow &&
              !showDetailsRow &&
              (isHubPresentation ? (
                <>
                  <CarePillActionButton
                    label={effectivePendingLabel}
                    onPress={() => onComplete(localData)}
                    variant="primary"
                    minHeight={54}
                    disabled={!canComplete}
                  />
                  {missingRequiredField ? (
                    <Text
                      style={{
                        fontSize: 12,
                        color: c.label,
                        textAlign: "center",
                        marginTop: 4,
                      }}
                    >
                      {t("care.treatment_flows.state.required_field", {
                        params: { field: missingRequiredField.label },
                      })}
                    </Text>
                  ) : null}
                </>
              ) : (
                <Pressable
                  onPress={() => {
                    if (!canComplete) return;
                    capturePress(pressLabel(effectivePendingLabel));
                    // Pass buffered field data with the complete call (single API request)
                    onComplete(localData);
                  }}
                  disabled={!canComplete}
                  style={({ pressed }) => ({
                    paddingVertical: 14,
                    borderRadius: 12,
                    backgroundColor: !canComplete
                      ? "rgba(74, 222, 128, 0.08)"
                      : pressed
                        ? "rgba(74, 222, 128, 0.3)"
                        : "rgba(74, 222, 128, 0.2)",
                    borderWidth: 1,
                    borderColor: "rgba(74, 222, 128, 0.4)",
                    alignItems: "center",
                    flexDirection: "row",
                    justifyContent: "center",
                    gap: 8,
                    opacity: canComplete ? 1 : 0.5,
                  })}
                >
                  <Check size={16} color={greenAccent} strokeWidth={2.5} />
                  <Text
                    style={{
                      fontSize: 14,
                      fontWeight: "600",
                      color: greenAccent,
                    }}
                  >
                    {effectivePendingLabel}
                  </Text>
                </Pressable>
              ))}

            {/* Skip link.
                Not suppressed by the scheduling row. A step that declares itself
                skippable is skippable precisely when there is nothing to
                schedule: `sperm_collection` on frozen sperm is the case that
                found this — no collection to date, and the row offering to
                schedule one was the only thing on the card, so there was no way
                to say it will not happen. The same holds for a `transfer` in the
                two flows that mark it skippable: a cycle freezing everything has
                no transfer to book either.
                `showOutcomeRow` still suppresses it — that row appears once the
                procedure has happened, and skipping something already recorded
                is not a thing to offer. */}
            {step.skippable &&
              !hideSkip &&
              !showOutcomeRow &&
              (isHubPresentation ? (
                <CarePillActionButton
                  label={
                    step.skipText ??
                    t("care.treatment_flows.actions.default_skip")
                  }
                  onPress={onSkip}
                  variant="secondary"
                  minHeight={54}
                />
              ) : (
                <Pressable
                  onPress={() => {
                    capturePress(
                      pressLabel(
                        step.skipText ??
                          t("care.treatment_flows.actions.default_skip"),
                      ),
                      "care.treatment_flows.actions.default_skip",
                    );
                    onSkip();
                  }}
                  style={({ pressed }) => ({
                    alignItems: "center",
                    paddingVertical: 8,
                    opacity: pressed ? 0.6 : 1,
                  })}
                >
                  <Text
                    style={{
                      fontSize: 13,
                      color: c.skipText,
                      textDecorationLine: "underline",
                    }}
                  >
                    {step.skipText ??
                      t("care.treatment_flows.actions.default_skip")}
                  </Text>
                </Pressable>
              ))}
          </View>
        </>
      )}
    </View>
  );
}

// =============================================================================
// Wizard Footer Component
// =============================================================================

interface WizardFooterProps {
  activeStepIndex: number;
  totalSteps: number;
  currentStepStatus: TreatmentStepStatus | "pending";
  isTreatmentComplete: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onEnterWaitingPhase: () => void;
  onDiscontinue?: () => void;
  goal: "pregnancy" | "preserve";
}

function WizardFooter({
  activeStepIndex,
  totalSteps,
  currentStepStatus,
  isTreatmentComplete,
  onPrevious,
  onNext,
  onEnterWaitingPhase,
  onDiscontinue,
  goal,
}: WizardFooterProps) {
  const canGoNext =
    currentStepStatus === "completed" || currentStepStatus === "skipped";
  const isLastStep = activeStepIndex === totalSteps - 1;
  const capturePress = usePressCapture("WizardFooter");

  return (
    <View
      style={{
        borderTopWidth: 1,
        borderTopColor: "rgba(255, 255, 255, 0.05)",
        backgroundColor: "rgba(15, 23, 42, 0.95)",
        borderBottomLeftRadius: 24,
        borderBottomRightRadius: 24,
      }}
    >
      {/* Nav row */}
      <View className="flex-row items-center justify-between px-6 py-3.5">
        {/* Previous Button */}
        <Pressable
          onPress={() => {
            capturePress("Back");
            onPrevious();
          }}
          disabled={activeStepIndex === 0}
          className="flex-row items-center gap-1 rounded-lg px-3 py-2"
          style={({ pressed }) => ({
            opacity: activeStepIndex === 0 ? 0.3 : pressed ? 0.7 : 1,
          })}
        >
          <ChevronLeft size={16} color="#94a3b8" />
          <Text style={{ fontSize: 13, color: "#94a3b8", fontWeight: "500" }}>
            Back
          </Text>
        </Pressable>

        {/* Step Counter */}
        <Text style={{ fontSize: 11, color: "#64748b", fontWeight: "600" }}>
          {activeStepIndex + 1} / {totalSteps}
        </Text>

        {/* Next / Complete Button */}
        {isTreatmentComplete && isLastStep ? (
          <Pressable
            onPress={() => {
              capturePress(goal === "preserve" ? "Complete" : "Waiting Phase");
              onEnterWaitingPhase();
            }}
            className="flex-row items-center gap-1 rounded-lg px-3 py-2"
            style={({ pressed }) => ({
              backgroundColor: pressed
                ? "rgba(196, 181, 253, 0.15)"
                : "rgba(196, 181, 253, 0.1)",
            })}
          >
            <Text
              style={{ fontSize: 13, color: violetAccent, fontWeight: "600" }}
            >
              {goal === "preserve" ? "Complete" : "Waiting Phase"}
            </Text>
            <ChevronRight size={16} color={violetAccent} />
          </Pressable>
        ) : (
          <Pressable
            onPress={() => {
              capturePress("Next");
              onNext();
            }}
            disabled={!canGoNext || isLastStep}
            className="flex-row items-center gap-1 rounded-lg px-3 py-2"
            style={({ pressed }) => ({
              backgroundColor:
                canGoNext && !isLastStep
                  ? pressed
                    ? "rgba(74, 222, 128, 0.15)"
                    : "rgba(74, 222, 128, 0.1)"
                  : "transparent",
              opacity: !canGoNext || isLastStep ? 0.3 : 1,
            })}
          >
            <Text
              style={{
                fontSize: 13,
                color: canGoNext && !isLastStep ? greenAccent : "#64748b",
                fontWeight: "500",
              }}
            >
              Next
            </Text>
            <ChevronRight
              size={16}
              color={canGoNext && !isLastStep ? greenAccent : "#64748b"}
            />
          </Pressable>
        )}
      </View>

      {/* Discontinue link */}
      {onDiscontinue && (
        <View
          style={{
            borderTopWidth: 1,
            borderTopColor: "rgba(255, 255, 255, 0.03)",
            paddingVertical: 10,
            alignItems: "center",
          }}
        >
          <Pressable
            onPress={() => {
              capturePress("Discontinue treatment");
              onDiscontinue();
            }}
            style={({ pressed }) => ({
              paddingVertical: 4,
              paddingHorizontal: 12,
              borderRadius: 6,
              opacity: pressed ? 0.6 : 1,
            })}
          >
            <Text
              style={{
                fontSize: 11,
                fontWeight: "500",
                color: "rgba(244, 63, 94, 0.6)",
              }}
            >
              Discontinue treatment
            </Text>
          </Pressable>
        </View>
      )}
    </View>
  );
}

// =============================================================================
// Helper: Get first pending step index
// =============================================================================

function getFirstPendingStepIndex(
  steps: TreatmentStepConfig[],
  stepProgress: TreatmentStepProgress[] | null,
): number {
  for (let i = 0; i < steps.length; i++) {
    const sp = stepProgress?.find((s) => s.stepId === steps[i]!.id);
    if (!sp || sp.status === "pending") return i;
  }
  return steps.length - 1; // All complete, show last step
}

// =============================================================================
// Main Modal Component
// =============================================================================

export type NavigationResult = {
  success: boolean;
  error?: string;
};

export interface TreatmentProgressModalProps {
  visible: boolean;
  onClose: () => void;
  cycleId: string | null;
  cycleType: CycleType;
  treatmentName: string;
  stepProgress: TreatmentStepProgress[] | null;
  onUpdateStepProgress: (
    stepId: string,
    data: Partial<TreatmentStepProgress>,
  ) => void;
  onNavigateToCare?: (
    context: TreatmentMedicationContext,
  ) => Promise<NavigationResult> | void;
  initialExpandedStep?: string | null;
  onEnterWaitingPhase?: () => void;
  onDiscontinue?: () => void;
  goal?: "pregnancy" | "preserve";
  pgtPlan?: PgtPlan | null;
}

export function TreatmentProgressModal({
  visible,
  onClose,
  cycleId,
  cycleType,
  treatmentName,
  stepProgress,
  onUpdateStepProgress,
  onNavigateToCare,
  initialExpandedStep,
  onEnterWaitingPhase,
  onDiscontinue,
  goal = "pregnancy",
  pgtPlan = null,
}: TreatmentProgressModalProps) {
  const t = useTranslations();
  const capturePress = usePressCapture("TreatmentProgressModal");

  // API client for fetching medications
  const { client, tenantId, patientId } = useApiClient();

  // Phase hooks for ensuring phase exists before adding medications
  const { data: phases = [] } = usePhases(cycleId);
  const { mutateAsync: createPhase } = useCreatePhase();

  // Get the flow for this cycle type
  const steps = useMemo(
    () => getStepsForCycle(cycleType, "self", t, { phases, pgtPlan }),
    [cycleType, phases, pgtPlan, t],
  );

  // Calculate first pending step index
  const firstPendingIndex = useMemo(
    () => getFirstPendingStepIndex(steps, stepProgress),
    [steps, stepProgress],
  );

  // Active step index for wizard navigation
  const [activeStepIndex, setActiveStepIndex] = useState<number>(() => {
    if (initialExpandedStep) {
      const idx = steps.findIndex((s) => s.id === initialExpandedStep);
      if (idx >= 0) return idx;
    }
    return getFirstPendingStepIndex(steps, stepProgress);
  });

  // Keep activeStepIndex in bounds when step progress changes (e.g. undo)
  useEffect(() => {
    if (activeStepIndex > firstPendingIndex) {
      setActiveStepIndex(firstPendingIndex);
    }
  }, [firstPendingIndex, activeStepIndex]);

  // Update active step when initialExpandedStep changes (return flow from Care)
  useEffect(() => {
    if (initialExpandedStep && visible) {
      const idx = steps.findIndex((s) => s.id === initialExpandedStep);
      if (idx >= 0) setActiveStepIndex(idx);
    }
  }, [initialExpandedStep, visible, steps]);

  // Navigation status notification
  const [navigationNotice, setNavigationNotice] = useState<{
    visible: boolean;
    message: string;
    type: "info" | "error";
  }>({ visible: false, message: "", type: "info" });

  // Waiting phase confirmation modal state
  const [showWaitingModal, setShowWaitingModal] = useState(false);

  // Fetch medications from API when not in local dev mode
  const { data: apiMedications } = useQuery({
    queryKey: ["medications", tenantId, patientId, cycleId],
    queryFn: async (): Promise<LocalMedication[]> => {
      if (!cycleId) return [];

      try {
        const response = await client.GET(
          "/{tenantId}/patients/{patientId}/medications",
          {
            params: {
              path: { tenantId, patientId },
            },
          },
        );

        if (response.error) {
          throwApiError(
            response.error,
            "Failed to load treatment medications",
            "TreatmentProgressModal",
            response.response?.status,
          );
        }

        const allMeds = response.data?.data ?? [];
        const cycleMeds = allMeds.filter((med) => med.cycleId === cycleId);

        return cycleMeds.map((med): LocalMedication => ({
          id: med.id,
          name:
            med.displayName ??
            med.medication?.name ??
            med.rawMedicationName ??
            "Unknown Medication",
          startDate: med.startDate,
          endDate: med.endDate ?? null,
          timeTaken: Array.isArray(med.timeTaken) ? med.timeTaken : [],
          dose: med.dose ?? med.medication?.dose ?? null,
          doseUnit: med.doseUnit ?? med.medication?.doseUnit ?? null,
          intakeType: med.route ?? null,
          notes: med.notes,
          createdAt: med.createdAt,
          cycleId: med.cycleId ?? null,
          treatmentStepId: med.phaseId ?? null,
          medicationRole:
            (med.medication?.type != null && (med.medication.type & 8) !== 0) ||
            med.medication?.moleculeType === "hcg"
              ? "trigger_shot"
              : null,
        }));
      } catch (error) {
        throwApiError(
          error,
          "Failed to load treatment medications",
          "TreatmentProgressModal",
          undefined,
        );
      }
    },
    enabled: visible && !!cycleId && !!tenantId && !!patientId,
    staleTime: 30000,
  });

  // Auto-dismiss navigation notice
  useEffect(() => {
    if (navigationNotice.visible) {
      const timer = setTimeout(() => {
        setNavigationNotice((prev) => ({ ...prev, visible: false }));
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [navigationNotice.visible]);

  // Track medications linked to treatment steps
  const [stepMedications, setStepMedications] = useState<
    Record<string, LocalMedication[]>
  >({});

  useEffect(() => {
    if (!visible || !cycleId) return;

    const flowSteps = steps;

    if (apiMedications) {
      const medsMap: Record<string, LocalMedication[]> = {};
      for (const step of flowSteps) {
        // Get the phase UUID for this step from step progress
        const sp = stepProgress?.find((s) => s.stepId === step.id);
        const stepPhaseId = sp?.phaseId;
        medsMap[step.id] = apiMedications.filter((med) =>
          stepPhaseId ? med.treatmentStepId === stepPhaseId : false,
        );
      }
      setStepMedications(medsMap);
    }
  }, [visible, cycleId, cycleType, apiMedications, stepProgress, steps]);

  // Calculate overall progress
  const progress = useMemo(() => {
    if (!stepProgress || steps.length === 0) return 0;
    const completedCount = stepProgress.filter(
      (s) => s.status === "completed" || s.status === "skipped",
    ).length;
    return Math.round((completedCount / steps.length) * 100);
  }, [stepProgress, steps]);

  // Get current step (first pending step)
  const currentStepId = useMemo(() => {
    if (!steps.length) return null;
    for (const step of steps) {
      const sp = stepProgress?.find((s) => s.stepId === step.id);
      if (!sp || sp.status === "pending") return step.id;
    }
    return null;
  }, [stepProgress, steps]);

  // Get current step name
  const currentStepName = useMemo(() => {
    if (!currentStepId) {
      if (!steps.length) return "Getting Started";
      return "All steps complete";
    }
    return (
      steps.find((s) => s.id === currentStepId)?.title ?? "Getting Started"
    );
  }, [currentStepId, steps]);

  // Treatment complete?
  const isTreatmentComplete = useMemo(() => {
    if (!steps.length) return false;
    return currentStepId === null;
  }, [currentStepId, steps]);

  // Handle entering waiting phase
  const handleEnterWaitingPhase = useCallback(() => {
    setShowWaitingModal(false);
    onClose();
    onEnterWaitingPhase?.();
  }, [onClose, onEnterWaitingPhase]);

  // Get step progress by ID
  const getStepProgress = useCallback(
    (stepId: string): TreatmentStepProgress | null => {
      return stepProgress?.find((s) => s.stepId === stepId) ?? null;
    },
    [stepProgress],
  );

  // Handle step completion + auto-advance
  const handleCompleteStep = useCallback(
    (stepId: string) => {
      onUpdateStepProgress(stepId, {
        status: "completed",
        completedAt: new Date().toISOString(),
      });
      // Auto-advance after brief delay
      setTimeout(() => {
        setActiveStepIndex((prev) => {
          const nextIndex = prev + 1;
          return nextIndex < steps.length ? nextIndex : prev;
        });
      }, 300);
    },
    [onUpdateStepProgress, steps.length],
  );

  // Handle step skip + auto-advance
  const handleSkipStep = useCallback(
    (stepId: string) => {
      onUpdateStepProgress(stepId, {
        status: "skipped",
        completedAt: new Date().toISOString(),
      });
      setTimeout(() => {
        setActiveStepIndex((prev) => {
          const nextIndex = prev + 1;
          return nextIndex < steps.length ? nextIndex : prev;
        });
      }, 300);
    },
    [onUpdateStepProgress, steps.length],
  );

  // Handle undo
  const handleUndoStep = useCallback(
    (stepId: string) => {
      onUpdateStepProgress(stepId, {
        status: "pending",
        completedAt: null,
      });
    },
    [onUpdateStepProgress],
  );

  // Handle data updates
  const handleUpdateData = useCallback(
    (
      stepId: string,
      data: Record<string, string | number | boolean | null>,
    ) => {
      onUpdateStepProgress(stepId, { data });
    },
    [onUpdateStepProgress],
  );

  // Ensure a phase exists for a step, creating it if needed. Returns the phase ID.
  const ensurePhaseExists = useCallback(
    async (stepId: string): Promise<string | null> => {
      if (!cycleId) return null;
      // A patient-reported FET transfer is recorded as a typed procedure, not
      // as a generic treatment phase created while handing off medication.
      if (cycleType === "fet" && stepId === "transfer") return null;
      const phaseType = getPhaseTypeForStep(stepId);
      if (!phaseType) return null;

      const sp = stepProgress?.find((s) => s.stepId === stepId);
      if (sp?.phaseId) return sp.phaseId;

      const existingPhase = phases.find((p) => p.type === phaseType);
      if (existingPhase) return existingPhase.id;

      try {
        const today = getTodayString();
        const newPhase = await createPhase({
          cycleId,
          body: { type: phaseType, startDate: today },
        });
        return newPhase.id;
      } catch (err) {
        console.warn("[TreatmentProgressModal] Failed to create phase:", err);
        return null;
      }
    },
    [cycleId, cycleType, stepProgress, phases, createPhase],
  );

  // Hand medication creation to the host so it can open the canonical Care modal.
  const requestMedicationHandoff = useCallback(
    async (
      stepId: string,
      options?: {
        medicationRole?: "standard" | "trigger_shot";
        medicationTypeFilter?: number;
      },
    ) => {
      if (!cycleId) {
        setNavigationNotice({
          visible: true,
          message: "Unable to link medication - no active treatment cycle",
          type: "error",
        });
        return;
      }
      const step = steps.find((s) => s.id === stepId);
      if (!step) return;
      const phaseId = await ensurePhaseExists(stepId);

      const context: TreatmentMedicationContext = {
        cycleId,
        stepId,
        phaseId,
        stepName: step.title,
        ...(options?.medicationRole
          ? { medicationRole: options.medicationRole }
          : {}),
        ...(typeof options?.medicationTypeFilter === "number"
          ? { medicationTypeFilter: options.medicationTypeFilter }
          : {}),
      };

      if (!onNavigateToCare) {
        setNavigationNotice({
          visible: true,
          message: "Open Care to add this medication to your schedule",
          type: "info",
        });
        return;
      }

      try {
        const result = await onNavigateToCare(context);
        if (result?.success === false) {
          setNavigationNotice({
            visible: true,
            message: result.error ?? "Unable to open medication entry",
            type: "error",
          });
        }
      } catch (err) {
        setNavigationNotice({
          visible: true,
          message:
            err instanceof Error
              ? err.message
              : "Unable to open medication entry",
          type: "error",
        });
      }
    },
    [cycleId, ensurePhaseExists, onNavigateToCare, steps],
  );

  const handleAddMedication = useCallback(
    async (stepId: string) => {
      await requestMedicationHandoff(stepId);
    },
    [requestMedicationHandoff],
  );

  const handleAddTriggerShot = useCallback(
    async (stepId: string) => {
      await requestMedicationHandoff(stepId, {
        medicationRole: "trigger_shot",
        medicationTypeFilter: 8,
      });
    },
    [requestMedicationHandoff],
  );

  // Get medications linked to a step
  const getLinkedMedications = useCallback(
    (stepId: string): LocalMedication[] => {
      return stepMedications[stepId] ?? [];
    },
    [stepMedications],
  );

  // Step navigation handler (with gating)
  const handleStepNavigation = useCallback(
    (targetIndex: number) => {
      if (targetIndex <= firstPendingIndex) {
        setActiveStepIndex(targetIndex);
      }
    },
    [firstPendingIndex],
  );

  // Current active step
  const activeStep = steps[activeStepIndex];
  const activeStepProgress = activeStep ? getStepProgress(activeStep.id) : null;

  // Step transition animation
  const fadeAnim = useRef(new RNAnimated.Value(1)).current;
  const slideAnim = useRef(new RNAnimated.Value(0)).current;
  const prevStepIndexRef = useRef(activeStepIndex);

  useEffect(() => {
    if (prevStepIndexRef.current === activeStepIndex) return;
    const direction = activeStepIndex > prevStepIndexRef.current ? 1 : -1;
    prevStepIndexRef.current = activeStepIndex;

    // Start: fade out + slide slightly in direction of navigation
    fadeAnim.setValue(0);
    slideAnim.setValue(direction * 30);

    // Animate in
    RNAnimated.parallel([
      RNAnimated.timing(fadeAnim, {
        toValue: 1,
        duration: 200,
        useNativeDriver: true,
      }),
      RNAnimated.timing(slideAnim, {
        toValue: 0,
        duration: 200,
        useNativeDriver: true,
      }),
    ]).start();
  }, [activeStepIndex, fadeAnim, slideAnim]);

  // Modal entrance animation (scale + fade)
  const modalScaleAnim = useRef(new RNAnimated.Value(0.95)).current;
  const modalFadeAnim = useRef(new RNAnimated.Value(0)).current;

  useEffect(() => {
    if (visible) {
      modalScaleAnim.setValue(0.95);
      modalFadeAnim.setValue(0);
      RNAnimated.parallel([
        RNAnimated.spring(modalScaleAnim, {
          toValue: 1,
          friction: 8,
          tension: 100,
          useNativeDriver: true,
        }),
        RNAnimated.timing(modalFadeAnim, {
          toValue: 1,
          duration: 200,
          useNativeDriver: true,
        }),
      ]).start();
    }
  }, [visible, modalScaleAnim, modalFadeAnim]);

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
      statusBarTranslucent
    >
      <View className="flex-1 items-center justify-center bg-[#020617]/80 px-4">
        {/* Backdrop */}
        <Pressable
          className="absolute inset-0"
          onPress={() => {
            capturePress("dismiss_backdrop");
            onClose();
          }}
        />

        {/* Modal Content - wider container with entrance animation */}
        <RNAnimated.View
          className="relative w-full overflow-hidden border border-white/10 bg-[#0F172A] shadow-2xl"
          style={{
            maxWidth: 480,
            maxHeight: "90%",
            borderRadius: 24,
            opacity: modalFadeAnim,
            transform: [{ scale: modalScaleAnim }],
            ...(Platform.OS === "web" && {
              backdropFilter: "blur(16px)",
              backgroundColor: "rgba(15, 23, 42, 0.97)",
            }),
          }}
        >
          {/* Top glow line */}
          <LinearGradient
            colors={["transparent", "rgba(196, 181, 253, 0.5)", "transparent"]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              top: 0,
              height: 2,
              zIndex: 10,
            }}
          />

          {/* Compact Header */}
          <View
            className="flex-row items-center justify-between px-5 pb-3.5 pt-5"
            style={{
              borderBottomWidth: 1,
              borderBottomColor: "rgba(255, 255, 255, 0.05)",
            }}
          >
            {/* Left: Treatment info */}
            <View style={{ flex: 1 }}>
              <View className="mb-1.5 flex-row items-center gap-2">
                <Text
                  style={{
                    fontSize: 10,
                    fontWeight: "700",
                    color: violetAccent,
                    textTransform: "uppercase",
                    letterSpacing: 1,
                  }}
                >
                  {treatmentName}
                </Text>
                <View
                  className="flex-row items-center gap-1 rounded-full px-1.5 py-0.5"
                  style={{
                    backgroundColor: isTreatmentComplete
                      ? "rgba(74, 222, 128, 0.1)"
                      : "rgba(196, 181, 253, 0.1)",
                  }}
                >
                  <View
                    style={{
                      width: 5,
                      height: 5,
                      borderRadius: 2.5,
                      backgroundColor: isTreatmentComplete
                        ? greenAccent
                        : violetAccent,
                    }}
                  />
                  <Text
                    style={{
                      fontSize: 9,
                      fontWeight: "700",
                      color: isTreatmentComplete ? greenAccent : violetAccent,
                      textTransform: "uppercase",
                    }}
                  >
                    {isTreatmentComplete ? "Complete" : "In Progress"}
                  </Text>
                </View>
              </View>
              <Text
                style={{
                  fontSize: 17,
                  fontWeight: "500",
                  color: "#ffffff",
                  letterSpacing: -0.3,
                }}
              >
                {currentStepName}
              </Text>
            </View>

            {/* Close button */}
            <Pressable
              onPress={() => {
                capturePress("Close modal");
                onClose();
              }}
              hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
              accessibilityRole="button"
              accessibilityLabel="Close modal"
              style={({ pressed }) => ({
                width: 32,
                height: 32,
                borderRadius: 16,
                backgroundColor: pressed
                  ? "rgba(255, 255, 255, 0.1)"
                  : "rgba(255, 255, 255, 0.05)",
                alignItems: "center",
                justifyContent: "center",
                marginLeft: 12,
              })}
            >
              <X size={16} color="#64748b" />
            </Pressable>
          </View>

          {/* Progress Bar - thin, below header */}
          <View
            style={{
              height: 3,
              backgroundColor: "rgba(30, 41, 59, 0.5)",
            }}
          >
            <LinearGradient
              colors={[violetAccent, greenAccent]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 0 }}
              style={{
                height: "100%",
                width: `${progress}%`,
              }}
            />
          </View>

          {/* Step Indicator */}
          <TreatmentStepIndicator
            steps={steps}
            stepProgress={stepProgress}
            activeStepIndex={activeStepIndex}
            firstPendingIndex={firstPendingIndex}
            onStepPress={handleStepNavigation}
          />

          {/* Navigation Notice Banner */}
          {navigationNotice.visible && (
            <View
              className="mx-4 mt-2 flex-row items-center gap-2.5 rounded-[10px] border p-3"
              style={{
                backgroundColor:
                  navigationNotice.type === "error"
                    ? "rgba(239, 68, 68, 0.1)"
                    : "rgba(59, 130, 246, 0.1)",
                borderColor:
                  navigationNotice.type === "error"
                    ? "rgba(239, 68, 68, 0.2)"
                    : "rgba(59, 130, 246, 0.2)",
              }}
            >
              {navigationNotice.type === "error" ? (
                <AlertCircle size={16} color="#ef4444" />
              ) : (
                <Clock size={16} color="#3b82f6" />
              )}
              <Text
                style={{
                  flex: 1,
                  fontSize: 13,
                  color:
                    navigationNotice.type === "error" ? "#fca5a5" : "#93c5fd",
                  lineHeight: 18,
                }}
              >
                {navigationNotice.message}
              </Text>
              <Pressable
                onPress={() => {
                  capturePress("dismiss_navigation_notice");
                  setNavigationNotice((prev) => ({ ...prev, visible: false }));
                }}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <X
                  size={14}
                  color={
                    navigationNotice.type === "error" ? "#fca5a5" : "#93c5fd"
                  }
                />
              </Pressable>
            </View>
          )}

          {/* Step Content (single step view) */}
          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={{
              padding: 24,
              paddingBottom: 24,
            }}
          >
            <RNAnimated.View
              style={{
                opacity: fadeAnim,
                transform: [{ translateX: slideAnim }],
              }}
            >
              {activeStep && (
                <StepWizardContent
                  step={activeStep}
                  progress={activeStepProgress}
                  isCurrentStep={activeStep.id === currentStepId}
                  onComplete={() => handleCompleteStep(activeStep.id)}
                  onSkip={() => handleSkipStep(activeStep.id)}
                  onUndo={() => handleUndoStep(activeStep.id)}
                  onUpdateData={(data) => handleUpdateData(activeStep.id, data)}
                  onAddMedication={() => handleAddMedication(activeStep.id)}
                  onAddTriggerShot={
                    activeStep.hasTriggerShot
                      ? () => handleAddTriggerShot(activeStep.id)
                      : undefined
                  }
                  linkedMedications={getLinkedMedications(activeStep.id)}
                />
              )}
            </RNAnimated.View>
          </ScrollView>

          {/* Wizard Footer */}
          <WizardFooter
            activeStepIndex={activeStepIndex}
            totalSteps={steps.length}
            currentStepStatus={activeStepProgress?.status ?? "pending"}
            isTreatmentComplete={isTreatmentComplete}
            onPrevious={() =>
              setActiveStepIndex((prev) => Math.max(0, prev - 1))
            }
            onNext={() =>
              setActiveStepIndex((prev) =>
                prev + 1 < steps.length ? prev + 1 : prev,
              )
            }
            onEnterWaitingPhase={() => setShowWaitingModal(true)}
            onDiscontinue={
              onDiscontinue
                ? () => {
                    onClose();
                    onDiscontinue();
                  }
                : undefined
            }
            goal={goal}
          />
        </RNAnimated.View>

        {/* Waiting Phase Confirmation Modal */}
        <ConfirmationModal
          visible={showWaitingModal}
          onClose={() => setShowWaitingModal(false)}
          onConfirm={handleEnterWaitingPhase}
          modalTitle={
            goal === "preserve"
              ? "Eggs Retrieved & Stored?"
              : "Is your treatment phase complete?"
          }
          modalDescription={
            goal === "preserve"
              ? "You're about to mark your egg freezing journey as complete. Your eggs are now safely stored for when you're ready to use them."
              : "You're about to move into the waiting period. During this time, you can continue tracking symptoms, notes, and any important details while you wait for your results."
          }
          modalConfirmText={
            goal === "preserve" ? "Yes, complete" : "Yes, continue"
          }
          modalCancelText="Not yet"
          modalHelperText="You can always change your journey stage from the My Journey section."
        />
      </View>
    </Modal>
  );
}

// =============================================================================
// Egg Freezing Migration: 3-step → 5-step
// =============================================================================

/**
 * Migrates old 3-step egg_freezing stepProgress to the new 5-step format.
 * Idempotent — if new-format IDs already exist, returns the array as-is.
 *
 * Mapping:
 *   old `protocol`       → `stimulation` (medications carry over)
 *   old `protocol` data  → `trigger_shot` entry if trigger fields present
 *   old `egg_retrieval`  → narrowed `egg_retrieval` + new `freezing`
 */
export function migrateEggFreezingStepProgress(
  stepProgress: TreatmentStepProgress[] | null,
): TreatmentStepProgress[] {
  if (!stepProgress || stepProgress.length === 0) return stepProgress ?? [];

  // Already migrated — new-format IDs present
  if (
    stepProgress.some(
      (s) =>
        s.stepId === "stimulation" ||
        s.stepId === "trigger_shot" ||
        s.stepId === "freezing",
    )
  ) {
    return stepProgress;
  }

  const result: TreatmentStepProgress[] = [];

  for (const sp of stepProgress) {
    if (sp.stepId === "protocol") {
      // protocol → stimulation (keep everything except trigger fields)
      const stimData = { ...(sp.data ?? {}) };
      delete stimData.triggerTime;
      delete stimData.triggerDate;
      delete stimData.triggerMedication;
      result.push({ ...sp, stepId: "stimulation", data: stimData });

      // Extract trigger data into a trigger_shot entry if present
      const oldData = sp.data ?? {};
      if (oldData.triggerTime || oldData.triggerDate) {
        result.push({
          stepId: "trigger_shot",
          status: "completed" as const,
          completedAt: sp.completedAt,
          medications: [],
          notes: null,
          data: {
            triggerDate: (oldData.triggerDate as string) ?? null,
            triggerTime: (oldData.triggerTime as string) ?? null,
            triggerMedication: (oldData.triggerMedication as string) ?? null,
          },
        });
      }
    } else if (sp.stepId === "egg_retrieval") {
      const oldData = sp.data ?? {};

      // Narrowed egg_retrieval — only retrieval-specific fields
      result.push({
        ...sp,
        stepId: "egg_retrieval",
        data: {
          eggsCollected: oldData.eggsCollected ?? null,
          procedureDate: oldData.procedureDate ?? null,
          notes: oldData.notes ?? null,
        },
      });

      // New freezing step — maturity/cryo fields
      const hasFreezeData =
        oldData.matureEggs != null ||
        oldData.eggsFrozen != null ||
        oldData.storageLocation != null ||
        oldData.batchId != null;
      if (hasFreezeData) {
        result.push({
          stepId: "freezing",
          status: sp.status,
          completedAt: sp.completedAt,
          medications: [],
          notes: null,
          data: {
            matureEggs: oldData.matureEggs ?? null,
            eggsFrozen: oldData.eggsFrozen ?? null,
            storageLocation: oldData.storageLocation ?? null,
            batchId: oldData.batchId ?? null,
          },
        });
      }
    } else {
      result.push(sp);
    }
  }

  return result;
}

export { TREATMENT_FLOWS } from "./treatmentFlows";

// =============================================================================
// Translation layer for TREATMENT_FLOWS
// =============================================================================

/**
 * Returns t(key) if a translation exists, otherwise returns the English
 * fallback. This lets us add translation keys incrementally without breaking
 * screens when a key hasn't been added yet.
 */
